import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  collection, 
  getDocs,
  query,
  where,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db, functions } from './firebase';
import { createOrUpdateContact, addContactTag, updateContactCustomFields } from './activeCampaignService';
import {
  getCachedCheckpoint,
  saveCachedCheckpoint,
  getCachedConfigItems,
  saveCachedConfigItems,
} from './browserStorageService';
import {
  getAdEntryCheckpointSnapshot,
  getTypesSnapshotItems,
} from '../data/adEntrySnapshots';

// Helper function to convert Firebase Timestamp to JavaScript Date
export const convertFirebaseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // If it's already a JavaScript Date
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // If it's a Firebase Timestamp
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's a timestamp object with seconds property
  if (timestamp && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
  }
  
  // Try to convert as regular date
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? null : date;
};

// Generate a unique 6-character shortcode (crypto-strong; sync — safe for ad cold path)
export const generateShortcode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 6; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(bytes[i] % chars.length);
  }
  return result;
};

// Check if shortcode already exists
export const checkShortcodeExists = async (shortcode) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', shortcode));
    return userDoc.exists();
  } catch (error) {
    console.error('Error checking shortcode:', error);
    return false;
  }
};

// Get user data by shortcode
export const getUserByShortcode = async (shortcode) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', shortcode));
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data();
    
    // Convert Firebase timestamps to JavaScript dates
    const processedData = {
      ...userData,
      startTime: convertFirebaseTimestamp(userData.startTime),
      lastActive: convertFirebaseTimestamp(userData.lastActive),
      journalEntries: userData.journalEntries?.map(entry => ({
        ...entry,
        timestamp: convertFirebaseTimestamp(entry.timestamp)
      })) || [],
      calculatedMetrics: {
        ...userData.calculatedMetrics,
        lastCalculated: convertFirebaseTimestamp(userData.calculatedMetrics?.lastCalculated)
      }
    };
    
    return processedData;
  } catch (error) {
    console.error('Error fetching user by shortcode:', error);
    throw error;
  }
};

// Generate unique shortcode — usually one existence check (crypto space is huge)
export const generateUniqueShortcode = async () => {
  for (let attempts = 0; attempts < 5; attempts++) {
    const shortcode = generateShortcode();
    // Skip network check on first try when caller will setDoc; still verify to avoid rare overwrite
    const exists = await checkShortcodeExists(shortcode);
    if (!exists) {
      return shortcode;
    }
  }

  throw new Error('Unable to generate unique shortcode');
};

const buildNewUserSessionData = (userShortcode) => ({
  shortcode: userShortcode,
  currentScreen: null,
  completedScreens: [],
  userInfo: {},
  responses: {},
  journalEntries: [],
  calculatedMetrics: {},
  startTime: new Date(),
  lastActive: new Date(),
  completedFunnel: false,
  hostname: window.location.hostname,
  userAgent: navigator.userAgent,
});

// Create new user session. If shortcode is provided and already exists, return existing.
export const createUserSession = async (shortcode = null) => {
  try {
    let userShortcode = shortcode;

    if (userShortcode) {
      const existing = await getUserSession(userShortcode);
      if (existing) {
        return existing;
      }
    } else {
      userShortcode = await generateUniqueShortcode();
    }

    const userData = buildNewUserSessionData(userShortcode);
    await setDoc(doc(db, 'users', userShortcode), userData);
    return userData;
  } catch (error) {
    console.error('Error creating user session:', error);
    // Race: another tab/path may have created the same shortcode
    if (shortcode) {
      try {
        const existing = await getUserSession(shortcode);
        if (existing) {
          return existing;
        }
      } catch (_) {
        // fall through
      }
    }
    throw error;
  }
};

/** Local-only session used when Firestore is slow/unavailable (fail-open cold path). */
export const buildOptimisticUserSession = (shortcode) =>
  buildNewUserSessionData(shortcode);

// Get user session data
export const getUserSession = async (shortcode) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', shortcode));
    
    if (!userDoc.exists()) {
      return null;
    }
    
    return userDoc.data();
  } catch (error) {
    console.error('Error getting user session:', error);
    throw error;
  }
};

/**
 * Normalize userInfo.goals map into an ordered list of goal strings.
 * @param {Record<string, unknown>|null|undefined} goalsMap
 * @returns {string[]}
 */
export const goalsMapToList = (goalsMap) => {
  if (!goalsMap || typeof goalsMap !== 'object' || Array.isArray(goalsMap)) {
    return [];
  }

  return Object.keys(goalsMap)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => {
      const value = goalsMap[key];
      if (typeof value === 'string') return value.trim();
      if (value && typeof value === 'object') {
        const label = value.label || value.text || value.goal || value.name;
        return typeof label === 'string' ? label.trim() : '';
      }
      return '';
    })
    .filter(Boolean);
};

/**
 * Load goals from users/{shortcode}.userInfo.goals in Firestore.
 * @param {string} shortcode
 * @returns {Promise<string[]>}
 */
export const fetchUserInfoGoals = async (shortcode) => {
  if (!shortcode) return [];
  try {
    const session = await getUserSession(shortcode);
    return goalsMapToList(session?.userInfo?.goals);
  } catch (error) {
    console.error('Error fetching userInfo.goals:', error);
    return [];
  }
};

// Update user progress
export const updateUserProgress = async (shortcode, updates) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    try {
      await updateDoc(userRef, {
        ...updates,
        lastActive: new Date()
      });
    } catch (updateError) {
      // Doc may not exist yet on optimistic ad cold path
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        await createUserSession(shortcode);
        await updateDoc(userRef, {
          ...updates,
          lastActive: new Date()
        });
      } else {
        throw updateError;
      }
    }
  } catch (error) {
    console.error('Error updating user progress:', error);
    throw error;
  }
};

/**
 * Record a paid advertising click from /fwd.
 * - Merges platform click IDs into currentAttribution (only the platforms present on this hit)
 * - Overwrites shared currentAttribution metadata (capturedAt, landingPage, utms) for this click
 * - Appends one or more clickEvents (append-only history)
 */
export const recordAdvertisingClick = async (shortcode, { params, landingPage, referrer }) => {
  try {
    const now = new Date();
    const utms = {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_content: params.get('utm_content') || null,
      utm_term: params.get('utm_term') || null
    };

    const fbclid = params.get('fbclid');
    const gclid = params.get('gclid');
    const gbraid = params.get('gbraid');
    const wbraid = params.get('wbraid');
    const liFatId = params.get('li_fat_id');
    const safeReferrer = referrer || null;

    const updates = {
      lastActive: now,
      'currentAttribution.capturedAt': now,
      'currentAttribution.landingPage': landingPage,
      'currentAttribution.utm_source': utms.utm_source,
      'currentAttribution.utm_medium': utms.utm_medium,
      'currentAttribution.utm_campaign': utms.utm_campaign,
      'currentAttribution.utm_content': utms.utm_content,
      'currentAttribution.utm_term': utms.utm_term
    };

    const events = [];

    if (fbclid) {
      updates['currentAttribution.fbclid'] = fbclid;
      events.push({
        timestamp: now,
        platform: 'meta',
        clickId: fbclid,
        landingPage,
        referrer: safeReferrer,
        ...utms
      });
    }

    if (gclid || gbraid || wbraid) {
      if (gclid) updates['currentAttribution.gclid'] = gclid;
      if (gbraid) updates['currentAttribution.gbraid'] = gbraid;
      if (wbraid) updates['currentAttribution.wbraid'] = wbraid;
      events.push({
        timestamp: now,
        platform: 'google',
        clickId: gclid || gbraid || wbraid,
        gclid: gclid || null,
        gbraid: gbraid || null,
        wbraid: wbraid || null,
        landingPage,
        referrer: safeReferrer,
        ...utms
      });
    }

    if (liFatId) {
      updates['currentAttribution.li_fat_id'] = liFatId;
      events.push({
        timestamp: now,
        platform: 'linkedin',
        clickId: liFatId,
        landingPage,
        referrer: safeReferrer,
        ...utms
      });
    }

    if (events.length === 0) {
      events.push({
        timestamp: now,
        platform: 'unknown',
        clickId: null,
        landingPage,
        referrer: safeReferrer,
        ...utms
      });
    }

    updates.clickEvents = arrayUnion(...events);

    await updateDoc(doc(db, 'users', shortcode), updates);
  } catch (error) {
    console.error('Error recording advertising click:', error);
    throw error;
  }
};

// Save Stripe transaction details to Firebase
export const saveStripeDetails = async (shortcode, paymentDetails) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    
    // Determine if this is a subscription or one-time payment
    const isSubscription = !!paymentDetails.subscriptionId;
    const checkpointType = isSubscription 
      ? (paymentDetails.screenKey === 'payment_membership' ? 'payment_membership' : 'subscription')
      : 'payment';
    
    // Build base stripe data
    const stripeData = {
      transactionId: paymentDetails.transactionId,
      paymentIntentId: paymentDetails.paymentIntent?.id,
      customerEmail: paymentDetails.customerEmail,
      customerName: paymentDetails.customerName,
      status: 'succeeded',
      createdAt: new Date(),
      lastUpdated: new Date(),
      // Additional Stripe-specific details for future reference
      paymentMethodId: paymentDetails.paymentMethodId,
      customer: {
        email: paymentDetails.customerEmail,
        name: paymentDetails.customerName
      },
      // Funnel context
      funnel: {
        shortcode: shortcode,
        screenKey: paymentDetails.screenKey || null,
        checkpointType: checkpointType
      }
    };
    
    // Add subscription-specific fields if it's a subscription
    if (isSubscription) {
      stripeData.subscriptionId = paymentDetails.subscriptionId;
      stripeData.customerId = paymentDetails.customerId;
      stripeData.priceId = paymentDetails.priceId;
      stripeData.membershipOption = paymentDetails.membershipOption || null;
    } else {
      // Add one-time payment fields (only if amount exists)
      if (paymentDetails.amount !== undefined && paymentDetails.amount !== null) {
        stripeData.amount = paymentDetails.amount; // Amount in cents (Stripe format)
        stripeData.dollarAmount = (paymentDetails.amount / 100).toFixed(2); // Amount in dollars for display
      }
      stripeData.productId = paymentDetails.productId;
    }
    
    // Add payment intent details if available
    if (paymentDetails.paymentIntent) {
      stripeData.paymentIntent = {
        id: paymentDetails.paymentIntent?.id,
        status: paymentDetails.paymentIntent?.status,
        amount: paymentDetails.paymentIntent?.amount,
        currency: paymentDetails.paymentIntent?.currency,
        created: paymentDetails.paymentIntent?.created
      };
    }
    
    // Remove any undefined values to avoid Firebase errors
    Object.keys(stripeData).forEach(key => {
      if (stripeData[key] === undefined) {
        delete stripeData[key];
      }
    });
    
    // Also clean nested objects
    if (stripeData.paymentIntent) {
      Object.keys(stripeData.paymentIntent).forEach(key => {
        if (stripeData.paymentIntent[key] === undefined) {
          delete stripeData.paymentIntent[key];
        }
      });
    }
    if (stripeData.customer) {
      Object.keys(stripeData.customer).forEach(key => {
        if (stripeData.customer[key] === undefined) {
          delete stripeData.customer[key];
        }
      });
    }
    if (stripeData.funnel) {
      Object.keys(stripeData.funnel).forEach(key => {
        if (stripeData.funnel[key] === undefined) {
          delete stripeData.funnel[key];
        }
      });
    }
    
    // Update the user's document with Stripe details at the top level
    const updateData = {
      stripeDetails: stripeData,
      lastActive: new Date()
    };

    // Also persist billing name/email into userInfo (approved charges)
    if (paymentDetails.customerEmail) {
      updateData['userInfo.email'] = String(paymentDetails.customerEmail).trim();
    }
    if (paymentDetails.customerName) {
      updateData['userInfo.name'] = String(paymentDetails.customerName).trim();
    }
    
    await updateDoc(userRef, updateData);
    
    return stripeData;
  } catch (error) {
    console.error('Error saving Stripe details:', error);
    throw error;
  }
};

// Get Stripe details for a user
export const getStripeDetails = async (shortcode) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data();
    return userData.stripeDetails || null;
  } catch (error) {
    console.error('Error getting Stripe details:', error);
    return null;
  }
};

// Get screen data by key
export const getScreenData = async (screenKey) => {
  try {
    // Add cache-busting by including updatedAt in the query
    const q = query(collection(db, 'screens'), where('key', '==', screenKey));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Return the first match (should be unique)
    const doc = querySnapshot.docs[0];
    const data = { id: doc.id, ...doc.data() };
    
    // Add a timestamp to force React to recognize data changes
    data._lastUpdated = Date.now();
    
    return data;
  } catch (error) {
    console.error('Error getting screen data:', error);
    throw error;
  }
};

// Get checkpoint data by key
export const getCheckpointData = async (checkpointKey, { allowStale = true } = {}) => {
  const stale =
    getCachedCheckpoint(checkpointKey) ||
    (allowStale ? getAdEntryCheckpointSnapshot(checkpointKey) : null);

  try {
    const q = query(collection(db, 'checkpoints'), where('key', '==', checkpointKey));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return stale;
    }

    // Return the first match (should be unique)
    const checkpointDoc = querySnapshot.docs[0];
    const data = { id: checkpointDoc.id, ...checkpointDoc.data() };
    saveCachedCheckpoint(checkpointKey, data);
    return data;
  } catch (error) {
    console.error('Error getting checkpoint data:', error);
    if (stale) {
      return stale;
    }
    throw error;
  }
};

/** Sync warm paint for ad-entry keys — cache or bundled snapshot, no network. */
export const getWarmCheckpointData = (checkpointKey) =>
  getCachedCheckpoint(checkpointKey) || getAdEntryCheckpointSnapshot(checkpointKey);

// True when nextScreen points at an external URL instead of a screen/checkpoint key
export const isExternalUrl = (value) => {
  if (typeof value !== 'string') return false;
  return /^https?:\/\//i.test(value.trim());
};

// Determine next screen based on conditional navigation
// Returns a screen/checkpoint key, an external URL (http/https), or null
export const getNextScreen = (screenOrCheckpointData, userResponse = null) => {
  const nextScreen = screenOrCheckpointData.nextScreen;
  
  // Simple string - screen key or external URL
  if (typeof nextScreen === 'string') {
    return nextScreen;
  }
  
  // Conditional navigation object
  if (typeof nextScreen === 'object' && nextScreen !== null) {
    // If we have a user response, check for conditional paths
    if (userResponse !== null && nextScreen.conditions) {
      const conditionalPath = nextScreen.conditions[userResponse];
      if (conditionalPath !== undefined) {
        return conditionalPath;
      }
    }
    
    // Fall back to default path
    return nextScreen.default || null;
  }
  
  return null;
};

// Get all screens (for admin/testing purposes)
export const getAllScreens = async () => {
  try {
    const screensSnapshot = await getDocs(collection(db, 'screens'));
    const screens = {};
    
    screensSnapshot.forEach((doc) => {
      const data = doc.data();
      screens[data.key] = { id: doc.id, ...data };
    });
    
    return screens;
  } catch (error) {
    console.error('Error getting all screens:', error);
    throw error;
  }
};

// Get all checkpoints (for admin/testing purposes)
export const getAllCheckpoints = async () => {
  try {
    const checkpointsSnapshot = await getDocs(collection(db, 'checkpoints'));
    const checkpoints = {};
    
    checkpointsSnapshot.forEach((doc) => {
      const data = doc.data();
      checkpoints[data.key] = { id: doc.id, ...data };
    });
    
    return checkpoints;
  } catch (error) {
    console.error('Error getting all checkpoints:', error);
    throw error;
  }
};

// Save checkpoint response
export const saveCheckpointResponse = async (shortcode, response) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    let userDoc = await getDoc(userRef);
    
    // Optimistic / ad cold path may paint before Firestore create finishes
    if (!userDoc.exists()) {
      await createUserSession(shortcode);
      userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error('User session not found');
      }
    }
    
    const userData = userDoc.data();
    const currentResponses = userData.responses || {};
    const updatedResponses = {
      ...currentResponses,
      [response.checkpointKey]: response
    };
    
    // Log the response being saved to verify selectedOptionText is included

    
    // Prepare base updates
    let updates = {
      responses: updatedResponses,
      lastActive: new Date()
    };
    
    // For email_capture checkpoints, also save user data to userInfo
    if (response.type === 'email_capture' && response.formData) {
      console.log('Processing email_capture userInfo update');
      const currentUserInfo = userData.userInfo || {};
      const updatedUserInfo = { ...currentUserInfo };
      
      // Save email, name, and phone if provided
      if (response.formData.email) {
        updatedUserInfo.email = response.formData.email;
      }
      if (response.formData.name) {
        updatedUserInfo.name = response.formData.name;
      }
      if (response.formData.phone) {
        updatedUserInfo.phone = response.formData.phone;
      }
      
      updates.userInfo = updatedUserInfo;
      
      // Queue ActiveCampaign integration to async retry service (don't wait for it)
      if (response.formData.email) {
        // Get checkpoint data to check for tag ID
        // Parse checkpoint key properly - it might be a full path or just an ID
        const checkpointKey = response.checkpointKey.includes('/') 
          ? response.checkpointKey.split('/').pop() 
          : response.checkpointKey;
        
        // Use the proper function to get checkpoint data by key
        const checkpointData = await getCheckpointData(checkpointKey);
        
        // Prepare contact data for ActiveCampaign
        // Handle name splitting properly
        let firstName = '';
        let lastName = '';
        if (response.formData.name && response.formData.name.trim()) {
          const nameParts = response.formData.name.trim().split(' ');
          if (nameParts.length > 1) {
            firstName = nameParts.slice(0, -1).join(' '); // Everything except last word
            lastName = nameParts[nameParts.length - 1]; // Last word
          } else {
            firstName = response.formData.name.trim();
            lastName = '';
          }
        }
        
        // Prepare phone number with country code if available
        let phone = response.formData.phone || '';
        if (phone && response.formData.countryCode) {
          phone = `${response.formData.countryCode}${phone.replace(/\D/g, '')}`;
        }
        
        // Prepare contact data
        const contactData = {
          email: response.formData.email,
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          shortCode: shortcode,
          tagName: checkpointData?.content?.activeCampaignTagId || null
        };
        
        // Get current metrics for custom fields update
        const mentalResilienceScore = userData.calculatedMetrics?.mentalResilience?.score;
        const optimalPaceFormatted = userData.calculatedMetrics?.optimalPace?.optimalHoursFormatted;
        
        // Queue the sync to async retry service
        const pendingSyncs = userData.pendingActiveCampaignSync || [];
        pendingSyncs.push({
          type: 'email_capture',
          data: {
            ...contactData,
            // Include custom fields data for later update
            customFields: {
              mentalResilience: mentalResilienceScore,
              optimalPace: optimalPaceFormatted,
              shortCode: shortcode
            }
          },
          timestamp: Date.now()
        });
        updates.pendingActiveCampaignSync = pendingSyncs;
        
        console.log('Queued ActiveCampaign sync for email capture (async processing)');
      }
    }
    
    // For phone_verification checkpoints, also save user data to userInfo
    if (response.type === 'phone_verification' && response.formData) {
      console.log('Processing phone_verification userInfo update');
      console.log('Phone verification form data:', response.formData);
      const currentUserInfo = userData.userInfo || {};
      const updatedUserInfo = { ...currentUserInfo };
      
      // Save email, name, and phone if provided
      if (response.formData.email) {
        console.log('Updating email in userInfo:', response.formData.email);
        updatedUserInfo.email = response.formData.email;
      } else {
        console.log('No email provided in form data');
      }
      if (response.formData.name) {
        console.log('Updating name in userInfo:', response.formData.name);
        updatedUserInfo.name = response.formData.name;
      }
      if (response.formData.phone) {
        // Store phone number separately from country code
        const phoneDigits = response.formData.phone.replace(/\D/g, '');
        console.log('Updating phone in userInfo:', phoneDigits);
        updatedUserInfo.phone = phoneDigits;
      }
      if (response.formData.countryCode) {
        console.log('Updating countryCode in userInfo:', response.formData.countryCode);
        updatedUserInfo.countryCode = response.formData.countryCode;
      }
      
      console.log('Updated userInfo:', updatedUserInfo);
      updates.userInfo = updatedUserInfo;
    }
    
    // For membership_entry screens, also save user data to userInfo
    if (response.type === 'membership_entry' && response.formData) {
      console.log('Processing membership_entry userInfo update');
      console.log('Membership entry form data:', response.formData);
      const currentUserInfo = userData.userInfo || {};
      const updatedUserInfo = { ...currentUserInfo };
      
      // Save all provided fields
      if (response.formData.name) {
        console.log('Updating name in userInfo:', response.formData.name);
        updatedUserInfo.name = response.formData.name;
      }
      if (response.formData.email) {
        console.log('Updating email in userInfo:', response.formData.email);
        updatedUserInfo.email = response.formData.email;
      }
      if (response.formData.phone) {
        console.log('Updating phone in userInfo:', response.formData.phone);
        updatedUserInfo.phone = response.formData.phone;
      }
      if (response.formData.countryCode) {
        console.log('Updating countryCode in userInfo:', response.formData.countryCode);
        updatedUserInfo.countryCode = response.formData.countryCode;
      }
      if (response.formData.location) {
        console.log('Updating location in userInfo:', response.formData.location);
        updatedUserInfo.location = response.formData.location;
      }
      if (response.formData.profession) {
        console.log('Updating profession in userInfo:', response.formData.profession);
        updatedUserInfo.profession = response.formData.profession;
      }
      
      console.log('Updated userInfo:', updatedUserInfo);
      updates.userInfo = updatedUserInfo;
    }

    // For goals checkpoints, save top goals into userInfo.goals
    if (response.type === 'goals' && Array.isArray(response.topGoals)) {
      const currentUserInfo = userData.userInfo || {};
      const goalsMap = {};
      response.topGoals.forEach((goal, index) => {
        if (typeof goal === 'string' && goal.trim()) {
          goalsMap[String(index + 1)] = goal.trim();
        }
      });
      const nextUserInfo = {
        ...currentUserInfo,
        goals: goalsMap,
      };
      if (
        typeof response.timeframeMonths === 'number' &&
        Number.isFinite(response.timeframeMonths)
      ) {
        nextUserInfo.goalTimeframeMonths = response.timeframeMonths;
      }
      updates.userInfo = nextUserInfo;
    }

    // Normalize {id,label}|string arrays into trimmed string labels
    const toLabelStrings = (items) => {
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => {
          if (typeof item === 'string') return item.trim();
          if (item && typeof item.label === 'string') return item.label.trim();
          return '';
        })
        .filter(Boolean);
    };

    // For speedbumps checkpoints, save summary + selections into userInfo
    if (response.type === 'speedbumps') {
      const currentUserInfo = updates.userInfo || userData.userInfo || {};
      updates.userInfo = {
        ...currentUserInfo,
        challengeChips: toLabelStrings(response.challengeChips),
        desiredChips: toLabelStrings(response.desiredChips),
        whereYouAre:
          typeof response.whereYouAre === 'string' ? response.whereYouAre : '',
        lookingForward:
          typeof response.lookingForward === 'string'
            ? response.lookingForward
            : '',
        speedbumps: toLabelStrings(response.speedbumps),
        warningsigns: toLabelStrings(response.warningSigns),
      };
    }

    // For which_are_you ("who") checkpoints, save selected roles into userInfo.who
    if (response.type === 'which_are_you' && Array.isArray(response.roles)) {
      const currentUserInfo = updates.userInfo || userData.userInfo || {};
      updates.userInfo = {
        ...currentUserInfo,
        who: toLabelStrings(response.roles),
      };
    }

    // For first_name checkpoints, save name into userInfo.name
    if (response.type === 'first_name' && response.formData?.name) {
      const currentUserInfo = userData.userInfo || {};
      updates.userInfo = {
        ...currentUserInfo,
        name: String(response.formData.name).trim(),
      };
    }
    
    // For phone_verification checkpoints, integrate with ActiveCampaign
    if (response.type === 'phone_verification' && response.formData) {
      // Declare variables outside try block so they're accessible in catch
      let contactId = userData.activeCampaignId;
      let checkpointData = null;
      
      try {
        // Determine which email to use - prioritize form data, fall back to user session
        const emailToUse = response.formData.email || userData.email || userData.userInfo?.email;
        
        if (!emailToUse) {
          console.warn('No email available for phone verification ActiveCampaign integration');
          return updates;
        }
        
        // Always update the contact with the latest information from the form
        const contactData = {
          email: emailToUse
        };
        
        // Add phone if provided
        if (response.formData.phone && response.formData.countryCode) {
          const fullPhoneNumber = `${response.formData.countryCode}${response.formData.phone.replace(/\D/g, '')}`;
          contactData.phone = fullPhoneNumber;
        }
        
        // Add name if provided - split into firstName and lastName
        if (response.formData.name && response.formData.name.trim()) {
          const nameParts = response.formData.name.trim().split(' ');
          if (nameParts.length > 1) {
            contactData.firstName = nameParts.slice(0, -1).join(' ');
            contactData.lastName = nameParts[nameParts.length - 1];
          } else {
            contactData.firstName = response.formData.name.trim();
            contactData.lastName = '';
          }
        } else if (userData.firstName || userData.lastName) {
          // Fallback to existing user data if no new name provided
          if (userData.firstName) contactData.firstName = userData.firstName;
          if (userData.lastName) contactData.lastName = userData.lastName;
        }
        
        // Create or update the contact with the latest information
        console.log('Creating/updating contact with data:', contactData);
        const contactResult = await createOrUpdateContact(contactData);
        console.log('Contact creation result:', contactResult);
        contactId = contactResult.contactId;
        
        // Store the contact ID for future use
        updates.activeCampaignId = contactId;
        
        // Get checkpoint data to check for tag ID
        const checkpointKey = response.checkpointKey.includes('/') 
          ? response.checkpointKey.split('/').pop() 
          : response.checkpointKey;
        checkpointData = await getCheckpointData(checkpointKey);
        
        // Add tag if specified in checkpoint
        if (checkpointData.content?.activeCampaignTagId && contactId) {
          const tagName = checkpointData.content.activeCampaignTagId;
          console.log('Attempting to add tag:', { contactId, tagName });
          
          try {
            await addContactTag(parseInt(contactId), null, tagName);
            console.log('Successfully added tag:', tagName, 'to contact:', contactId);
          } catch (tagError) {
            console.warn('Failed to add tag:', tagError);
            // Don't fail the entire integration if tag addition fails
          }
        }
        
        // Contact is automatically added to list 50 (Insatiable Funnel Email) by Cloud Functions
        
        // Always update custom fields - shortCode is always available, calculated metrics when available
        try {
          const mentalResilienceScore = userData.calculatedMetrics?.mentalResilience?.score;
          const optimalPaceFormatted = userData.calculatedMetrics?.optimalPace?.optimalHoursFormatted;
          
          // Update all custom fields in a single API call
          await updateContactCustomFields(contactId, {
            mentalResilience: mentalResilienceScore,
            optimalPace: optimalPaceFormatted,
            shortCode: shortcode
          });
        } catch (fieldsError) {
          console.warn('Failed to update custom fields for phone verification:', fieldsError);
          // Don't fail the entire integration if custom field update fails
        }
        
      } catch (error) {
        console.error('Failed to integrate phone verification with ActiveCampaign:', error);
        
        // Check if it's a CORS error (development issue)
        if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
          console.warn('CORS error detected - this is expected in development. ActiveCampaign integration will work in production.');
        }
        
        // Store the error for retry later
        const pendingSyncs = updates.pendingActiveCampaignSync || [];
        pendingSyncs.push({
          type: 'phone_verification',
          data: {
            contactId: contactId || userData.activeCampaignId,
            tagId: checkpointData?.content?.activeCampaignTagId
          },
          error: error.message,
          timestamp: Date.now()
        });
        updates.pendingActiveCampaignSync = pendingSyncs;
      }
    }
    
    // For payment checkpoints, integrate with ActiveCampaign if we have a contact
    if (response.type === 'payment' && response.formData && userData.activeCampaignId) {
      // Get checkpoint data to check for tag ID
      // Parse checkpoint key properly - it might be a full path or just an ID
      const checkpointKey = response.checkpointKey.includes('/') 
        ? response.checkpointKey.split('/').pop() 
        : response.checkpointKey;
      const checkpointData = await getCheckpointData(checkpointKey);
      
      try {
        // Add tag if specified in checkpoint
        if (checkpointData.content?.activeCampaignTagId) {
          // The activeCampaignTagId field contains the tag name, not the ID
          // We need to look up the tag ID first, then apply it
          const tagName = checkpointData.content.activeCampaignTagId;
          
          // Use the service to apply the tag
          await addContactTag(parseInt(userData.activeCampaignId), null, tagName);
        }
        
        // Contact is automatically added to list 50 (Insatiable Funnel Email) by Cloud Functions
        
        // Always update custom fields - shortCode is always available, calculated metrics when available
        try {
          const mentalResilienceScore = userData.calculatedMetrics?.mentalResilience?.score;
          const optimalPaceFormatted = userData.calculatedMetrics?.optimalPace?.optimalHoursFormatted;
          
          // Update all custom fields in a single API call
          await updateContactCustomFields(userData.activeCampaignId, {
            mentalResilience: mentalResilienceScore,
            optimalPace: optimalPaceFormatted,
            shortCode: shortcode
          });
        } catch (fieldsError) {
          console.warn('Failed to update custom fields for payment:', fieldsError);
          // Don't fail the entire integration if custom field update fails
        }
        
      } catch (error) {
        console.error('Failed to integrate payment with ActiveCampaign:', error);
        
        // Check if it's a CORS error (development issue)
        if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
          console.warn('CORS error detected - this is expected in development. ActiveCampaign integration will work in production.');
        }
        
        // Store the error for retry later
        const pendingSyncs = updates.pendingActiveCampaignSync || [];
        pendingSyncs.push({
          type: 'payment',
          data: {
            contactId: userData.activeCampaignId,
            tagId: checkpointData.content?.activeCampaignTagId
          },
          error: error.message,
          timestamp: Date.now()
        });
        updates.pendingActiveCampaignSync = pendingSyncs;
      }
    }
    
    await updateDoc(userRef, updates);
    
    return updatedResponses;
  } catch (error) {
    console.error('Error saving checkpoint response:', error);
    throw error;
  }
};

// Update the most recent journal entry with pointsPlan, hoursPlan, and/or other fields
export const updateMostRecentJournalEntryPlan = async (shortcode, { pointsPlan, hoursPlan, deepdivecode, ...otherFields } = {}) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    
    // Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    const existingJournalEntries = userData.journalEntries || [];
    
    // Check if there are any journal entries
    if (existingJournalEntries.length === 0) {
      return;
    }
    
    // Find the most recent journal entry index by comparing timestamps directly
    // We need to compare the original timestamp values, not converted ones
    let mostRecentIndex = 0;
    let mostRecentTimestamp = existingJournalEntries[0]?.timestamp || 0;
    
    // Convert to comparable values (milliseconds since epoch)
    const getTimestampValue = (timestamp) => {
      if (!timestamp) return 0;
      // If it's a Firebase Timestamp, convert it
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().getTime();
      }
      // If it's already a Date
      if (timestamp instanceof Date) {
        return timestamp.getTime();
      }
      // If it's a number (milliseconds), return as is
      if (typeof timestamp === 'number') {
        return timestamp;
      }
      // Try to parse as date
      return new Date(timestamp).getTime() || 0;
    };
    
    // Find the entry with the highest timestamp
    existingJournalEntries.forEach((entry, index) => {
      const entryTimestampValue = getTimestampValue(entry.timestamp);
      const currentMostRecentValue = getTimestampValue(mostRecentTimestamp);
      if (entryTimestampValue > currentMostRecentValue) {
        mostRecentIndex = index;
        mostRecentTimestamp = entry.timestamp;
      }
    });
    
    if (mostRecentIndex === -1 || mostRecentIndex >= existingJournalEntries.length) {
      return;
    }
    
    // Update the most recent entry with pointsPlan, hoursPlan, deepdivecode, and/or other fields
    const updatedEntries = [...existingJournalEntries];
    const updates = {};
    if (pointsPlan !== undefined) {
      updates.pointsPlan = pointsPlan;
    }
    if (hoursPlan !== undefined) {
      updates.hoursPlan = hoursPlan;
    }
    if (deepdivecode !== undefined) {
      updates.deepdivecode = deepdivecode;
    }
    // Add any other fields passed in
    Object.keys(otherFields).forEach(key => {
      if (otherFields[key] !== undefined) {
        updates[key] = otherFields[key];
      }
    });
    
    updatedEntries[mostRecentIndex] = {
      ...updatedEntries[mostRecentIndex],
      ...updates
    };
    
    // Update the user document
    await updateDoc(userRef, {
      journalEntries: updatedEntries,
      lastActive: new Date()
    });
    
    return updatedEntries[mostRecentIndex];
  } catch (error) {
    console.error('Error updating most recent journal entry plan:', error);
    throw error;
  }
};

// Get total points earned from completed commitments for today from founders-first project
// Uses Cloud Function to bypass security rules
export const getCompletedCommitmentsCount = async (shortcode) => {
  try {
    // Calculate today's date in the user's local timezone (YYYY/MM/DD format)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayDateString = `${year}/${month}/${day}`;
    
    // Get user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Call Cloud Function instead of direct database access
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('./firebase');
    
    const getCompletedCommitmentsFunction = httpsCallable(functions, 'getCompletedCommitmentsCount');
    const result = await getCompletedCommitmentsFunction({ 
      shortcode,
      todayDateString,
      timezone
    });
    
    return result.data?.totalPoints || 0;
  } catch (error) {
    console.error('Error getting completed commitments points:', error);
    // Fallback: return 0 if there's an error
    return 0;
  }
};

/**
 * Total points from completed commitments today in founders-first (by Firestore user id).
 * Uses the same callable as shortcode-based lookup; pass today's date in the user's IANA zone (YYYY/MM/DD).
 */
export const getCompletedCommitmentsPointsByUserId = async (userId, todayDateString, timeZone) => {
  try {
    if (!userId || typeof userId !== 'string' || !userId.trim()) {
      return 0;
    }
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('./firebase');
    const fn = httpsCallable(functions, 'getCompletedCommitmentsCount');
    const result = await fn({
      userId: userId.trim(),
      todayDateString,
      timezone: timeZone && typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : 'UTC'
    });
    return result.data?.totalPoints || 0;
  } catch (error) {
    console.error('Error getting completed commitments points by userId:', error);
    return 0;
  }
};

/** Flat emotional keys written by syncJournalEntriesToDory when spreading `emotionalStates` */
const DORY_FLAT_EMOTION_KEYS = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];

export const entryTimestampToMillis = (timestamp) => {
  const d = convertFirebaseTimestamp(timestamp);
  if (d) return d.getTime();
  if (typeof timestamp === 'number' && !Number.isNaN(timestamp)) return timestamp;
  return null;
};

/**
 * Map a founders-first `journals` document (as returned by getFoundersFirstJournalsForGroupsAdmin) into
 * the Insatiable embedded journal entry shape (nested emotionalStates, pressureScore).
 */
export const normalizeDoryJournalRowToInsatiableEntry = (row) => {
  if (!row || typeof row !== 'object') return null;
  const data = { ...row };
  delete data.id;

  const emotionalStates = {
    ...(data.emotionalStates && typeof data.emotionalStates === 'object' ? data.emotionalStates : {})
  };
  for (const k of DORY_FLAT_EMOTION_KEYS) {
    if (Object.prototype.hasOwnProperty.call(data, k)) {
      emotionalStates[k] = data[k];
      delete data[k];
    }
  }

  const pressureScore =
    data.pressureScore !== undefined && data.pressureScore !== null
      ? data.pressureScore
      : data.pressure;
  delete data.pressure;

  const ts = data.timestamp;
  delete data.timestamp;
  delete data.userId;
  delete data.date;
  delete data.time;
  delete data.syncedFromInsatiableAt;
  delete data.shortcode;

  // founders-first `journals` uses hoursPlanned / pointsNeeded; Insatiable users.journalEntries uses hoursPlan / pointsPlan
  if (
    (data.hoursPlan === undefined || data.hoursPlan === null) &&
    data.hoursPlanned !== undefined &&
    data.hoursPlanned !== null
  ) {
    data.hoursPlan = data.hoursPlanned;
  }
  delete data.hoursPlanned;

  if (
    (data.pointsPlan === undefined || data.pointsPlan === null) &&
    data.pointsNeeded !== undefined &&
    data.pointsNeeded !== null
  ) {
    data.pointsPlan = data.pointsNeeded;
  }
  delete data.pointsNeeded;

  return {
    ...data,
    emotionalStates,
    pressureScore,
    timestamp: ts
  };
};

/**
 * Milliseconds for a raw Dory row timestamp (number from Cloud Function or Firestore-like object).
 */
const doryRowTimestampToMillis = (row) => {
  if (!row || row.timestamp == null) return null;
  const t = row.timestamp;
  if (typeof t === 'number' && !Number.isNaN(t)) return t;
  if (typeof t === 'object' && typeof t.seconds === 'number') {
    return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6);
  }
  return null;
};

/**
 * Merge Insatiable `journalEntries` with founders-first rows. Skips Dory rows that duplicate Insatiable
 * when `sourceProject === "insatiable"` and `timestamp` aligns (same instant as an Insatiable entry).
 */
export const mergeInsatiableJournalEntriesWithDoryRows = (insatiableEntries, doryRows) => {
  const ins = Array.isArray(insatiableEntries) ? [...insatiableEntries] : [];
  const canonicalMs = new Set();
  for (const e of ins) {
    const ms = entryTimestampToMillis(e.timestamp);
    if (ms != null) canonicalMs.add(ms);
  }

  const extra = [];
  for (const row of Array.isArray(doryRows) ? doryRows : []) {
    const rawMs = doryRowTimestampToMillis(row);
    if (row && row.sourceProject === 'insatiable' && rawMs != null && canonicalMs.has(rawMs)) {
      continue;
    }
    const norm = normalizeDoryJournalRowToInsatiableEntry(row);
    if (norm) extra.push(norm);
  }

  const merged = [...ins, ...extra];
  merged.sort((a, b) => {
    const am = entryTimestampToMillis(a.timestamp) ?? 0;
    const bm = entryTimestampToMillis(b.timestamp) ?? 0;
    return am - bm;
  });

  return merged.map((entry) => ({
    ...entry,
    timestamp: convertFirebaseTimestamp(entry.timestamp) || entry.timestamp
  }));
};

/**
 * Batch-load founders-first journals for Groups admin (same callable + Admin SDK path as journal sync).
 */
export const getFoundersFirstJournalsForGroupsAdminBatch = async (shortcodes) => {
  const list = Array.isArray(shortcodes)
    ? [...new Set(shortcodes.map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean))]
    : [];
  if (list.length === 0) {
    return {};
  }
  try {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('./firebase');
    const fn = httpsCallable(functions, 'getFoundersFirstJournalsForGroupsAdmin');
    const result = await fn({ shortcodes: list });
    return result.data?.byShortcode || {};
  } catch (error) {
    console.error('Error loading founders-first journals for Groups admin:', error);
    return {};
  }
};

/**
 * Admin User Lookup: load a founders-first users/{userId} profile plus that user's journals.
 */
export const getFoundersFirstUserLookupById = async (userId) => {
  const id = typeof userId === 'string' ? userId.trim() : '';
  if (!id) {
    return null;
  }
  try {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('./firebase');
    const fn = httpsCallable(functions, 'getFoundersFirstUserLookupAdmin');
    const result = await fn({ userId: id });
    return result.data || null;
  } catch (error) {
    console.error('Error loading founders-first user lookup:', error);
    throw error;
  }
};

/**
 * Admin Optimal Pace Analysis: load lean founders-first journal histories for back-calculation.
 */
export const getFoundersFirstJournalsForOptimalPaceAnalysis = async () => {
  try {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('./firebase');
    const fn = httpsCallable(functions, 'getFoundersFirstOptimalPaceValuesAdmin', {
      timeout: 300000
    });
    const result = await fn({});
    const users = Array.isArray(result.data?.users) ? result.data.users : [];
    return {
      users,
      totalUsersWithJournals: result.data?.totalUsersWithJournals ?? users.length,
      totalJournalEntries: result.data?.totalJournalEntries ?? 0
    };
  } catch (error) {
    console.error('Error loading founders-first journals for optimal pace analysis:', error);
    throw error;
  }
};

/** Journal slider defaults used when a user never moves the check-in controls. */
const DEFAULT_JOURNAL_SLIDER_VALUE = 5;
const DEFAULT_JOURNAL_EMOTION_KEYS = [
  'mania',
  'depression',
  'energy',
  'focus',
  'calm',
  'happiness',
  'hours'
];

/**
 * True when a journal entry still has every emotion + pressure slider at the default (5).
 * hoursWorked / hoursWorkedRange are ignored — users can leave those alone and still be "default".
 */
export const isDefaultJournalEntry = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }

  const pressure =
    entry.pressureScore !== undefined && entry.pressureScore !== null
      ? entry.pressureScore
      : entry.pressure;
  if (Number(pressure) !== DEFAULT_JOURNAL_SLIDER_VALUE) {
    return false;
  }

  const emotionalStates =
    entry.emotionalStates && typeof entry.emotionalStates === 'object'
      ? entry.emotionalStates
      : entry;

  return DEFAULT_JOURNAL_EMOTION_KEYS.every(
    (key) => Number(emotionalStates[key]) === DEFAULT_JOURNAL_SLIDER_VALUE
  );
};

/** Most recent journal entry by timestamp, or null if none. */
export const getMostRecentJournalEntry = (journalEntries) => {
  if (!Array.isArray(journalEntries) || journalEntries.length === 0) {
    return null;
  }

  return journalEntries.reduce((best, entry) => {
    if (!best) return entry;
    const entryMs = entryTimestampToMillis(entry?.timestamp) ?? 0;
    const bestMs = entryTimestampToMillis(best?.timestamp) ?? 0;
    return entryMs >= bestMs ? entry : best;
  }, null);
};

/** True when the lookup input looks like an Insatiable 6-character shortcode. */
export const looksLikeInsatiableShortcode = (value) =>
  typeof value === 'string' && /^[A-Za-z0-9]{6}$/.test(value.trim());

/** Calendar date in an IANA zone, as YYYY/MM/DD for founders-first. */
export const formatDateYyyyMmDdSlashedInTimeZone = (date, ianaTimeZone) => {
  const tz =
    ianaTimeZone && typeof ianaTimeZone === 'string' && ianaTimeZone.trim()
      ? ianaTimeZone.trim()
      : 'UTC';
  const d = date instanceof Date ? date : new Date(date);
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(d);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (y && m && day) {
      return `${y}/${m}/${day}`;
    }
  } catch {
    // fall through
  }
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
};

export const formatTodayYyyyMmDdSlashedInTimeZone = (ianaTimeZone) =>
  formatDateYyyyMmDdSlashedInTimeZone(new Date(), ianaTimeZone);

/**
 * Calendar dates for accountability measurement: from start (inclusive) through yesterday
 * (inclusive), never including today.
 * With no goals doc, start is the calendar day one week before today.
 */
export const getAccountabilityMeasurementPeriodDates = (goalsDoc, ianaTimeZone) => {
  const tz =
    ianaTimeZone && typeof ianaTimeZone === 'string' && ianaTimeZone.trim()
      ? ianaTimeZone.trim()
      : 'UTC';
  const todayStr = formatTodayYyyyMmDdSlashedInTimeZone(tz);
  const [ty, tm, td] = todayStr.split('/').map((s) => parseInt(s, 10));
  if (!ty || !tm || !td) return [];

  const yesterdayUtc = new Date(Date.UTC(ty, tm - 1, td - 1, 12, 0, 0));
  const yesterdayStr = formatDateYyyyMmDdSlashedInTimeZone(yesterdayUtc, tz);

  let startDateStr;
  if (goalsDoc && goalsDoc.date && typeof goalsDoc.date === 'string') {
    startDateStr = goalsDoc.date;
  } else {
    const weekAgoUtc = new Date(Date.UTC(ty, tm - 1, td - 7, 12, 0, 0));
    startDateStr = formatDateYyyyMmDdSlashedInTimeZone(weekAgoUtc, tz);
  }

  if (!startDateStr || startDateStr > yesterdayStr) {
    return [];
  }

  const [sy, sm, sd] = startDateStr.split('/').map((s) => parseInt(s, 10));
  if (!sy || !sm || !sd) return [];

  const dates = [];
  for (let i = 0; i <= 366; i++) {
    const utc = new Date(Date.UTC(sy, sm - 1, sd + i, 12, 0, 0));
    const dateStr = formatDateYyyyMmDdSlashedInTimeZone(utc, tz);
    if (dateStr > yesterdayStr) break;
    dates.push(dateStr);
  }
  return dates;
};

const getCheckInsByDateForDateStrings = (journalEntries, dateStrings, ianaTimeZone) => {
  const tz =
    ianaTimeZone && typeof ianaTimeZone === 'string' && ianaTimeZone.trim()
      ? ianaTimeZone.trim()
      : 'UTC';
  const dateSet = new Set(dateStrings);
  const checkInByDate = new Map();

  for (const entry of journalEntries || []) {
    if (!entry.timestamp) continue;
    const date = convertFirebaseTimestamp(entry.timestamp);
    if (!date) continue;
    const dateKey = formatDateYyyyMmDdSlashedInTimeZone(date, tz);
    if (!dateSet.has(dateKey)) continue;
    const existing = checkInByDate.get(dateKey);
    const entryMs = entryTimestampToMillis(entry.timestamp) ?? 0;
    const existingMs = existing ? (entryTimestampToMillis(existing.timestamp) ?? 0) : -1;
    if (!existing || entryMs > existingMs) {
      checkInByDate.set(dateKey, entry);
    }
  }

  return checkInByDate;
};

/**
 * Batch-load accountability goals per founders-first user id.
 * Returns { prior, today } — prior excludes today; today is today's doc if any.
 */
export const parseAccountabilityBundle = (raw) => {
  if (!raw) return { prior: null, today: null };
  if (Object.prototype.hasOwnProperty.call(raw, 'prior') || Object.prototype.hasOwnProperty.call(raw, 'today')) {
    return { prior: raw.prior ?? null, today: raw.today ?? null };
  }
  return { prior: raw, today: null };
};

export const buildInitialGoalDraft = (member) => {
  const todayGoals = member?.todayAccountabilityGoals;
  const accomplishment = member?.goalAccomplishment;

  if (todayGoals) {
    const todayOpenItems = todayGoals.openItems ?? todayGoals['open items'] ?? {};
    const openItemLabels = Object.keys(todayOpenItems);
    const cis =
      todayGoals.cis != null && todayGoals.cis !== '' && !Number.isNaN(Number(todayGoals.cis))
        ? Number(todayGoals.cis)
        : (accomplishment?.actualCheckIns ?? 0);
    const pds =
      todayGoals.pds != null && todayGoals.pds !== '' && !Number.isNaN(Number(todayGoals.pds))
        ? Number(todayGoals.pds)
        : (accomplishment?.actualPeakDays ?? 0);
    return {
      cis,
      pds,
      openItems: openItemLabels.length > 0 ? [...openItemLabels, ''] : ['']
    };
  }

  return {
    cis: accomplishment?.actualCheckIns ?? 0,
    pds: accomplishment?.actualPeakDays ?? 0,
    openItems: ['']
  };
};

export const getAccountabilityForGroupsAdminBatch = async (userIds) => {
  const list = Array.isArray(userIds)
    ? [...new Set(userIds.map((id) => (typeof id === 'string' ? id.trim() : '')).filter(Boolean))]
    : [];
  if (list.length === 0) {
    return {};
  }
  try {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('./firebase');
    const fn = httpsCallable(functions, 'getAccountabilityForGroupsAdmin');
    const result = await fn({ userIds: list });
    return result.data?.byUserId || {};
  } catch (error) {
    console.error('Error loading accountability for Groups admin:', error);
    return {};
  }
};

/**
 * Save accountability goals for a founders-first user (creates or updates doc for that date).
 */
export const saveAccountabilityGoals = async (userId, { cis, pds, openItems, date, timeZone }) => {
  const trimmedUserId = typeof userId === 'string' ? userId.trim() : '';
  if (!trimmedUserId) {
    throw new Error('userId is required');
  }
  const normalizeDate = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed.replace(/-/g, '/');
    }
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    return null;
  };
  const dateStr = normalizeDate(date) || formatTodayYyyyMmDdSlashedInTimeZone(timeZone);

  const openItemsMap = {};
  if (Array.isArray(openItems)) {
    for (const item of openItems) {
      const label = typeof item === 'string' ? item.trim() : '';
      if (label) openItemsMap[label] = false;
    }
  } else if (openItems && typeof openItems === 'object') {
    for (const [key, val] of Object.entries(openItems)) {
      const label = typeof key === 'string' ? key.trim() : '';
      if (label) openItemsMap[label] = Boolean(val);
    }
  }

  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('./firebase');
  const fn = httpsCallable(functions, 'saveAccountabilityGoals');
  const result = await fn({
    userId: trimmedUserId,
    cis,
    pds,
    openItems: openItemsMap,
    date: dateStr
  });
  return result.data;
};

/**
 * Load items array from a configuration collection document.
 * Normalizes each entry to { id, label }.
 * @param {string} docId - Document id (e.g. 'speedbumps', 'warningsigns')
 * @returns {Promise<Array<{ id: string, label: string }>>}
 */
export const getConfigurationItems = async (docId) => {
  const stale =
    getCachedConfigItems(docId) ||
    (docId === 'types' ? getTypesSnapshotItems() : null);

  try {
    const snap = await getDoc(doc(db, 'configuration', docId));
    if (!snap.exists()) {
      console.warn(`Configuration document not found: ${docId}`);
      return stale || [];
    }

    const rawItems = snap.data()?.items;
    if (!Array.isArray(rawItems)) {
      return stale || [];
    }

    const items = rawItems
      .map((item, index) => {
        if (typeof item === 'string') {
          const label = item.trim();
          if (!label) return null;
          return { id: `item_${index}`, label };
        }
        if (!item || typeof item !== 'object') return null;

        const label = String(
          item.label || item.text || item.title || item.name || '',
        ).trim();
        if (!label) return null;

        const id = String(item.id || item.key || `item_${index}`);
        const chip = String(
          item.chip || item.shortLabel || item.challengeChip || '',
        ).trim();
        const desiredChip = String(
          item.desiredChip || item.desired || item.positiveChip || '',
        ).trim();

        return {
          id,
          label,
          ...(chip ? { chip } : {}),
          ...(desiredChip ? { desiredChip } : {}),
        };
      })
      .filter(Boolean);

    saveCachedConfigItems(docId, items);
    return items;
  } catch (error) {
    console.error(`Error loading configuration/${docId}:`, error);
    return stale || [];
  }
};

/** Sync warm paint for configuration/types — cache or bundled snapshot. */
export const getWarmConfigurationItems = (docId) => {
  const cached = getCachedConfigItems(docId);
  if (cached?.length) return cached;
  if (docId === 'types') return getTypesSnapshotItems();
  return [];
};

/**
 * Evaluate a top goal via Gemini Cloud Function.
 * @param {{ kind?: string, goal: string }} params
 * @returns {Promise<{ verdict: 'confirm' | 'challenge', message: string }>}
 */
export const evaluateGoal = async ({ kind = 'top', goal }) => {
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('./firebase');
  const fn = httpsCallable(functions, 'evaluateGoal');
  const result = await fn({
    kind,
    goal,
  });
  return result.data;
};

/**
 * Summarize top goals + speedbumps (+ optional warning signs) via Gemini Cloud Function.
 * topGoals is optional — when empty, summary speaks only to speedbumps / warning signs.
 * @param {{ topGoals?: string[], speedbumps: Array<string|{id?:string,label?:string}>, warningSigns?: Array<string|{id?:string,label?:string}> }} params
 * @returns {Promise<{ whereYouAre: string, lookingForward: string, challengeChips: string[], desiredChips: string[], summary: string }>}
 */
export const summarizeGoals = async ({ topGoals, speedbumps, warningSigns }) => {
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('./firebase');
  const fn = httpsCallable(functions, 'summarizeGoals');
  const result = await fn({
    topGoals: topGoals || [],
    speedbumps: speedbumps || [],
    warningSigns: warningSigns || [],
  });
  return result.data;
};

/**
 * Compare accountability goals against actual check-ins, peak days, and open items.
 * Period: goals doc date (inclusive) through yesterday, or one week before today through yesterday.
 */
export const computeAccountabilityGoalAccomplishment = async (
  goalsDoc,
  journalEntries,
  doryUserId,
  doryTimeZone
) => {
  const tz =
    doryTimeZone && typeof doryTimeZone === 'string' && doryTimeZone.trim()
      ? doryTimeZone.trim()
      : 'UTC';

  const hasGoalsDoc = goalsDoc && goalsDoc.date;
  const periodDates = getAccountabilityMeasurementPeriodDates(goalsDoc, tz);

  const checkInByDate = getCheckInsByDateForDateStrings(journalEntries, periodDates, tz);
  const actualCheckIns = checkInByDate.size;

  let actualPeakDays = 0;
  if (doryUserId && periodDates.length > 0) {
    const peakChecks = await Promise.all(
      periodDates.map(async (dateStr) => {
        const checkIn = checkInByDate.get(dateStr);
        if (!checkIn) return false;
        const pointsPlan = checkIn.pointsPlan;
        if (pointsPlan === null || pointsPlan === undefined) return false;
        const n = Number(pointsPlan);
        if (Number.isNaN(n)) return false;
        const completed = await getCompletedCommitmentsPointsByUserId(doryUserId, dateStr, tz);
        return completed >= n;
      })
    );
    actualPeakDays = peakChecks.filter(Boolean).length;
  }

  const cisGoal = hasGoalsDoc && goalsDoc.cis != null ? Number(goalsDoc.cis) : null;
  const pdsGoal = hasGoalsDoc && goalsDoc.pds != null ? Number(goalsDoc.pds) : null;
  const openItems = hasGoalsDoc && goalsDoc.openItems && typeof goalsDoc.openItems === 'object'
    ? goalsDoc.openItems
    : {};

  const openItemsTotal = Object.keys(openItems).length;
  const openItemsDone = Object.values(openItems).filter(Boolean).length;
  const openItemsMet = openItemsTotal > 0 ? openItemsDone === openItemsTotal : null;

  return {
    cisGoal: cisGoal != null && !Number.isNaN(cisGoal) ? cisGoal : null,
    pdsGoal: pdsGoal != null && !Number.isNaN(pdsGoal) ? pdsGoal : null,
    openItems,
    actualCheckIns,
    actualPeakDays,
    openItemsMet,
    openItemsDone,
    openItemsTotal
  };
};

/** Format actual count with optional goal, e.g. "5" or "5/9". */
export const formatAccountabilityCount = (actual, goal) => {
  const actualNum = actual ?? 0;
  if (goal == null || goal === '' || Number.isNaN(Number(goal))) {
    return String(actualNum);
  }
  return `${actualNum}/${Number(goal)}`;
};

export const isAccountabilityGoalMet = (actual, goal) => {
  if (goal == null || goal === '' || Number.isNaN(Number(goal))) {
    return false;
  }
  return (actual ?? 0) >= Number(goal);
};

const accountabilityDayOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/** Format YYYY/MM/DD as "May 20th". */
export const formatAccountabilityPriorDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const match = dateStr.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (!match) return '';
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (!month || !day) return '';
  const monthName = new Date(Date.UTC(2000, month - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'UTC'
  });
  return `${monthName} ${day}${accountabilityDayOrdinalSuffix(day)}`;
};

// Save journal entry and calculate optimal pace
export const saveJournalEntry = async (shortcode, journalData) => {
  try {
    const userRef = doc(db, 'users', shortcode);
    
    // Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    const existingJournalEntries = userData.journalEntries || [];
    
    // Add timestamp if not present and ensure hoursWorked is always an integer
    const journalEntry = {
      ...journalData,
      timestamp: journalData.timestamp || Date.now()
    };
    
    // Round hoursWorked to nearest integer if present
    if (journalEntry.hoursWorked !== null && journalEntry.hoursWorked !== undefined) {
      journalEntry.hoursWorked = Math.round(journalEntry.hoursWorked);
    }
    
    // Calculate mental resilience using the complete journal history
    const mentalResilience = calculateMentalResilienceWithHistory([...existingJournalEntries, journalEntry]);
    
    // Only calculate optimal pace if hours worked is present
    let optimalPace = null;
    let updates = {
      journalEntries: [...existingJournalEntries, journalEntry],
      lastActive: new Date(),
      calculatedMetrics: {
        ...userData.calculatedMetrics,
        mentalResilience: mentalResilience,
        lastMentalResilienceCalculation: Date.now()
      }
    };
    
    if (journalEntry.hoursWorked && journalEntry.hoursWorked > 0) {
      // Use enhanced optimal pace calculation with all journal entries
      // Filter to only entries with timestamp and hoursWorked (matching admin screen logic)
      // Convert Firebase timestamps to JavaScript dates for consistent date calculations
      const allJournalEntries = [...existingJournalEntries, journalEntry]
        .filter(entry => entry.timestamp && entry.hoursWorked)
        .sort((a, b) => {
          const aTime = convertFirebaseTimestamp(a.timestamp) || a.timestamp;
          const bTime = convertFirebaseTimestamp(b.timestamp) || b.timestamp;
          return new Date(aTime) - new Date(bTime);
        })
        .map(entry => ({
          ...entry,
          timestamp: convertFirebaseTimestamp(entry.timestamp) || entry.timestamp
        }));
      optimalPace = calculateEnhancedOptimalPace(allJournalEntries);
      
      // Fallback to legacy calculation if enhanced fails
      if (!optimalPace) {
        optimalPace = calculateOptimalPace(journalEntry);
      }
      
      // Calculate refresh rate for today
      const refreshRate = calculateRefreshRate(allJournalEntries);
      
      updates.calculatedMetrics = {
        ...updates.calculatedMetrics,
        optimalPace: optimalPace,
        lastOptimalPaceCalculation: Date.now(),
        refreshRate: refreshRate !== null ? refreshRate : undefined
      };
    }
    
    await updateDoc(userRef, updates);

    // Sync journal entries to Dory (founders-first) when user has appUserId.
    // First-time sync (when `synced` is missing): backfill all journal entries.
    // Ongoing sync (when `synced` exists): write only the new journal entry.
    if (userData.appUserId && functions) {
      try {
        if (!userData.synced) {
          await runInitialJournalSyncToDory({
            shortcode,
            appUserId: userData.appUserId,
            entries: [...existingJournalEntries, journalEntry]
          });
        } else {
          const { httpsCallable } = await import('firebase/functions');
          const syncJournalEntriesToDory = httpsCallable(functions, 'syncJournalEntriesToDory');
          await syncJournalEntriesToDory({
            shortcode,
            appUserId: userData.appUserId,
            entries: [journalEntry]
          });
        }
      } catch (dorySyncError) {
        console.error('Failed to sync journal entry to Dory project:', dorySyncError);
      }
    }
    
    // Sync calculated metrics to ActiveCampaign if we have a contact ID
    if (userData.activeCampaignId) {
      try {
        // Import the function here to avoid circular dependencies
        const { updateContactCustomFields } = await import('./activeCampaignService');
        
        // Update all custom fields in a single API call
        try {
          await updateContactCustomFields(userData.activeCampaignId, {
            mentalResilience: mentalResilience?.score,
            optimalPace: optimalPace?.optimalHoursFormatted,
            shortCode: shortcode
          });
        } catch (error) {
          console.error('Failed to update custom fields:', error);
        }
        
        // Clear any pending sync errors
        if (userData.pendingActiveCampaignSync) {
          await updateDoc(userRef, {
            pendingActiveCampaignSync: []
          });
        }
      } catch (error) {
        console.error('Failed to sync metrics to ActiveCampaign:', error);
        // Store the error for retry later
        const pendingSync = userData.pendingActiveCampaignSync || [];
        pendingSync.push({
          type: 'metrics_update',
          data: {
            mentalResilience,
            optimalPace,
            shortCode: shortcode
          },
          error: error.message,
          timestamp: Date.now()
        });
        
        await updateDoc(userRef, {
          pendingActiveCampaignSync: pendingSync
        });
      }
    }
    
    return {
      journalEntry,
      optimalPace,
      mentalResilience
    };
  } catch (error) {
    console.error('Error saving journal entry:', error);
    throw error;
  }
};

// Enhanced Optimal Pace calculation using multi-entry weighted regression
export const calculateEnhancedOptimalPace = (journalEntries, targetIndex = null) => {
  if (!journalEntries || journalEntries.length === 0) return null;
  
  // If targetIndex is null, use the last entry
  const actualTargetIndex = targetIndex !== null ? targetIndex : journalEntries.length - 1;
  const targetEntry = journalEntries[actualTargetIndex];
  
  if (!targetEntry || !targetEntry.emotionalStates || !targetEntry.hoursWorked) return null;
  
  try {
    // Get historical entries up to the target index
    const historicalEntries = journalEntries.slice(0, actualTargetIndex + 1);
    
    // Calculate time-weighted importance (exponential decay)
    const currentTime = new Date(targetEntry.timestamp);
    const lambda = 0.1; // Decay rate (adjustable)
    
    const weightedEntries = historicalEntries.map((entry, index) => {
      const entryTime = new Date(entry.timestamp);
      const daysDiff = (currentTime - entryTime) / (1000 * 60 * 60 * 24);
      const weight = Math.exp(-lambda * daysDiff);
      
      return {
        ...entry,
        weight,
        daysAgo: daysDiff
      };
    }).filter(entry => entry.weight > 0.01); // Only include entries with meaningful weight
    
    if (weightedEntries.length === 0) return null;
    
    // Calculate weighted averages for emotional factors
    const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
    const weightedEmotionalAverages = {};
    
    emotionalFactors.forEach(factor => {
      const validEntries = weightedEntries.filter(entry => 
        entry.emotionalStates && 
        entry.emotionalStates[factor] !== null && 
        entry.emotionalStates[factor] !== undefined
      );
      
      if (validEntries.length > 0) {
        const weightedSum = validEntries.reduce((sum, entry) => 
          sum + (entry.emotionalStates[factor] * entry.weight), 0
        );
        const totalWeight = validEntries.reduce((sum, entry) => sum + entry.weight, 0);
        weightedEmotionalAverages[factor] = weightedSum / totalWeight;
      }
    });
    
    // Calculate weighted pressure average
    const pressureEntries = weightedEntries.filter(entry => 
      entry.pressureScore !== null && entry.pressureScore !== undefined
    );
    
    let weightedPressureAverage = 5; // Default
    if (pressureEntries.length > 0) {
      const weightedPressureSum = pressureEntries.reduce((sum, entry) => 
        sum + (entry.pressureScore * entry.weight), 0
      );
      const totalPressureWeight = pressureEntries.reduce((sum, entry) => sum + entry.weight, 0);
      weightedPressureAverage = weightedPressureSum / totalPressureWeight;
    }
    
    // Calculate weighted hours worked average
    const hoursEntries = weightedEntries.filter(entry => 
      entry.hoursWorked !== null && entry.hoursWorked !== undefined
    );
    
    let weightedHoursAverage = 8; // Default
    if (hoursEntries.length > 0) {
      const weightedHoursSum = hoursEntries.reduce((sum, entry) => 
        sum + (entry.hoursWorked * entry.weight), 0
      );
      const totalHoursWeight = hoursEntries.reduce((sum, entry) => sum + entry.weight, 0);
      weightedHoursAverage = weightedHoursSum / totalHoursWeight;
    }
    
    // Calculate current emotional arousal (inverted scale)
    const currentEmotionalValues = emotionalFactors
      .map(factor => weightedEmotionalAverages[factor])
      .filter(val => val !== undefined)
      .map(val => 10 - val); // Invert for arousal calculation
    
    const currentArousal = currentEmotionalValues.length > 0 
      ? currentEmotionalValues.reduce((sum, val) => sum + val, 0) / currentEmotionalValues.length
      : 5;
    
    // Determine task complexity based on ACTUAL pressure score for this day
    const actualPressure = targetEntry.pressureScore;
    const isHighComplexity = actualPressure >= 6;
    
    // Calculate optimal arousal range based on actual pressure
    let optimalArousalRange;
    if (isHighComplexity) {
      optimalArousalRange = { min: 1.0, optimal: 2.0, max: 3.0 };
    } else {
      optimalArousalRange = { min: 1.0, optimal: 3.0, max: 5.0 };
    }
    
    // Calculate the middle of the optimal range as the target arousal
    const targetArousal = (optimalArousalRange.min + optimalArousalRange.max) / 2;
    
    // Calculate arousal ratio to move toward the middle of the optimal range
    const arousalRatio = targetArousal / Math.max(currentArousal, 0.1);
    
    // Apply pressure adjustment factor
    const pressureAdjustmentFactor = 1 + (weightedPressureAverage - 5) * 0.1; // ±10% adjustment based on pressure
    
    // Calculate optimal hours with pressure adjustment
    let optimalHours = weightedHoursAverage * arousalRatio * pressureAdjustmentFactor;
    
    // Apply caps
    const cappedArousalRatio = Math.max(0.5, Math.min(2.0, arousalRatio));
    optimalHours = Math.max(2, Math.min(18, optimalHours));
    
    // Calculate range based on optimal arousal range boundaries with same percentage cap
    const minArousalRatio = Math.max(0.5, Math.min(2.0, optimalArousalRange.min / Math.max(currentArousal, 0.1)));
    const maxArousalRatio = Math.max(0.5, Math.min(2.0, optimalArousalRange.max / Math.max(currentArousal, 0.1)));
    
    const minHours = Math.max(2, Math.min(18, weightedHoursAverage * minArousalRatio * pressureAdjustmentFactor));
    const maxHours = Math.max(2, Math.min(18, weightedHoursAverage * maxArousalRatio * pressureAdjustmentFactor));
    
    // Format hours and minutes as spelled-out text
    const formatHoursAndMinutes = (decimalHours) => {
      const wholeHours = Math.floor(decimalHours);
      const minutes = Math.round((decimalHours - wholeHours) * 60);
      
      const hourNames = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen'];
      const minuteNames = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'twenty-one', 'twenty-two', 'twenty-three', 'twenty-four', 'twenty-five', 'twenty-six', 'twenty-seven', 'twenty-eight', 'twenty-nine', 'thirty', 'thirty-one', 'thirty-two', 'thirty-three', 'thirty-four', 'thirty-five', 'thirty-six', 'thirty-seven', 'thirty-eight', 'thirty-nine', 'forty', 'forty-one', 'forty-two', 'forty-three', 'forty-four', 'forty-five', 'forty-six', 'forty-seven', 'forty-eight', 'forty-nine', 'fifty', 'fifty-one', 'fifty-two', 'fifty-three', 'fifty-four', 'fifty-five', 'fifty-six', 'fifty-seven', 'fifty-eight', 'fifty-nine'];
      
      if (wholeHours === 0) {
        return `${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
      } else if (minutes === 0) {
        return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
      } else {
        return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''} and ${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
    };
    
    // Determine pacing status
    const getPacingStatus = () => {
      const currentArousalValue = currentArousal;
      const optimalZoneMin = optimalArousalRange.min;
      const optimalZoneMax = optimalArousalRange.max;
      
      if (currentArousalValue > optimalZoneMax) {
        return {
          status: 'over',
          headline: 'Over Pacing',
          subheadline: 'You are eroding your Mental Resilience. Your Optimal Pace is slowing.'
        };
      } else if (currentArousalValue >= optimalZoneMin && currentArousalValue <= optimalZoneMax) {
        return {
          status: 'optimal',
          headline: 'At Optimal Pace',
          subheadline: 'You are perfectly supporting your Mental Resilience so your Optimal Pace can hold steady.'
        };
      } else {
        return {
          status: 'under',
          headline: 'Under Pacing',
          subheadline: 'You are restoring your Mental Resilience. Your Optimal Pace is increasing.'
        };
      }
    };
    
    const pacingStatus = getPacingStatus();
    
    // Calculate adjustment percentage
    const adjustmentPercentage = Math.round((cappedArousalRatio - 1) * 100);
    
    return {
      currentArousal: Math.round(currentArousal * 10) / 10, // Round to 1 decimal
      isHighComplexity,
      optimalHours: Math.round(optimalHours * 10) / 10, // Store as decimal
      optimalHoursFormatted: formatHoursAndMinutes(optimalHours), // Formatted for display
      hoursWorked: targetEntry.hoursWorked, // Store the original hours worked for reference
      hoursRange: {
        min: Math.round(minHours * 10) / 10, // Store as decimal
        max: Math.round(maxHours * 10) / 10, // Store as decimal
        minFormatted: formatHoursAndMinutes(minHours), // Formatted for display
        maxFormatted: formatHoursAndMinutes(maxHours) // Formatted for display
      },
      pacingStatus,
      adjustmentPercentage,
      arousalRatio: Math.round(cappedArousalRatio * 100) / 100,
      optimalArousalRange,
      timestamp: Date.now(),
      // Enhanced metadata
      enhanced: true,
      entriesUsed: weightedEntries.length,
      weightedPressure: Math.round(weightedPressureAverage * 10) / 10,
      actualPressure: actualPressure,
      targetArousal: Math.round(targetArousal * 10) / 10
    };
    
  } catch (error) {
    console.error('Error calculating enhanced optimal pace:', error);
    return null;
  }
};

/**
 * Back-calculate Optimal Pace for every journal entry (same approach as User Lookup chart).
 * Skips counting readings whose target entry still has all default slider values.
 * Default entries remain in history so later readings match User Lookup weighting.
 */
export const collectHistoricalOptimalPaceValues = (journalEntries) => {
  const entries = (Array.isArray(journalEntries) ? journalEntries : [])
    .filter((entry) => entry && entry.timestamp && entry.hoursWorked)
    .sort(
      (a, b) =>
        (entryTimestampToMillis(a.timestamp) ?? 0) - (entryTimestampToMillis(b.timestamp) ?? 0)
    );

  const values = [];
  let skippedDefault = 0;
  let skippedUncalculable = 0;

  for (let index = 0; index < entries.length; index++) {
    const entry = entries[index];
    if (isDefaultJournalEntry(entry)) {
      skippedDefault++;
      continue;
    }

    const prediction = calculateEnhancedOptimalPace(entries, index);
    if (!prediction || prediction.optimalHours === undefined || prediction.optimalHours === null) {
      skippedUncalculable++;
      continue;
    }

    const hours = Number(prediction.optimalHours);
    if (Number.isNaN(hours)) {
      skippedUncalculable++;
      continue;
    }

    values.push(hours);
  }

  return {
    values,
    skippedDefault,
    skippedUncalculable,
    entriesConsidered: entries.length
  };
};

// Calculate optimal pace based on journal entry data (legacy single-entry method)
export const calculateOptimalPace = (journalData) => {
  const {
    pressureScore: pressure,
    hoursWorked: hours,
    emotionalStates
  } = journalData;
  
  // Hours worked is guaranteed to be present at this point
  
  // Calculate average emotional arousal (excluding pressure)
  // Invert values since journal scale is inverse to arousal (10 - value)
  const emotionalValues = [
    emotionalStates?.focus,        // Focus
    emotionalStates?.calm,         // Calm
    emotionalStates?.happiness,    // Positivity
    emotionalStates?.depression,   // Satisfaction
    emotionalStates?.mania,        // Control
    emotionalStates?.energy,       // Energy
    emotionalStates?.hours         // Personal Identity (if available)
  ].filter(val => val !== '' && val !== null && val !== undefined)
    .map(val => 10 - parseFloat(val)) // Invert scale for arousal calculation
    .filter(val => !isNaN(val));
  
  const averageArousal = emotionalValues.length > 0 
    ? emotionalValues.reduce((sum, val) => sum + val, 0) / emotionalValues.length
    : 5; // Fallback if no emotional data
  
  // Determine task complexity based on pressure
  const isHighComplexity = pressure >= 6;
  
  // Yerkes-Dodson: High complexity tasks need lower optimal arousal and narrower range
  // Low complexity tasks can handle higher arousal with wider tolerance
  let optimalArousalRange;
  
  if (isHighComplexity) {
    // For complex tasks (high pressure), optimal arousal is 1-3 with optimal at 2
    optimalArousalRange = { min: 1.0, optimal: 2.0, max: 3.0 };
  } else {
    // For simple tasks (low pressure), optimal arousal is 1-5 with optimal at 3
    optimalArousalRange = { min: 1.0, optimal: 3.0, max: 5.0 };
  }
  
  // Calculate scaling ratio based on arousal relationship
  // If current arousal is higher than optimal, we need to reduce workload
  // If current arousal is lower than optimal, we can increase workload
  const arousalRatio = optimalArousalRange.optimal / Math.max(averageArousal, 0.1); // Avoid division by zero
  
  // Apply arousal ratio with caps: max 100% increase (2x) and max 18 hours for optimal
  const cappedArousalRatio = Math.min(2.0, arousalRatio); // Cap at 100% increase
  const optimalHours = Math.min(18, hours * cappedArousalRatio); // Cap optimal at 18 hours
  
  // Calculate range based on optimal arousal range boundaries with same percentage cap
  const minArousalRatio = Math.min(2.0, optimalArousalRange.min / Math.max(averageArousal, 0.1));
  const maxArousalRatio = Math.min(2.0, optimalArousalRange.max / Math.max(averageArousal, 0.1));
  
  const minHours = hours * minArousalRatio;
  const maxHours = Math.min(18, hours * maxArousalRatio); // Also cap max hours at 18
  
  // Format hours and minutes as spelled-out text
  const formatHoursAndMinutes = (decimalHours) => {
    const wholeHours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - wholeHours) * 60);
    
    const hourNames = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen'];
    const minuteNames = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'twenty-one', 'twenty-two', 'twenty-three', 'twenty-four', 'twenty-five', 'twenty-six', 'twenty-seven', 'twenty-eight', 'twenty-nine', 'thirty', 'thirty-one', 'thirty-two', 'thirty-three', 'thirty-four', 'thirty-five', 'thirty-six', 'thirty-seven', 'thirty-eight', 'thirty-nine', 'forty', 'forty-one', 'forty-two', 'forty-three', 'forty-four', 'forty-five', 'forty-six', 'forty-seven', 'forty-eight', 'forty-nine', 'fifty', 'fifty-one', 'fifty-two', 'fifty-three', 'fifty-four', 'fifty-five', 'fifty-six', 'fifty-seven', 'fifty-eight', 'fifty-nine'];
    
    if (wholeHours === 0) {
      return `${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (minutes === 0) {
      return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    } else {
      return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''} and ${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };
  
  // Determine pacing status
  const getPacingStatus = () => {
    const currentArousal = averageArousal;
    const optimalZoneMin = optimalArousalRange.min;
    const optimalZoneMax = optimalArousalRange.max;
    
    if (currentArousal > optimalZoneMax) {
      return {
        status: 'over',
        headline: 'Over Pacing',
        subheadline: 'You are eroding your Mental Resilience. Your Optimal Pace is slowing.'
      };
    } else if (currentArousal >= optimalZoneMin && currentArousal <= optimalZoneMax) {
      return {
        status: 'optimal',
        headline: 'At Optimal Pace',
        subheadline: 'You are perfectly supporting your Mental Resilience so your Optimal Pace can hold steady.'
      };
    } else {
      return {
        status: 'under',
        headline: 'Under Pacing',
        subheadline: 'You are restoring your Mental Resilience. Your Optimal Pace is increasing.'
      };
    }
  };
  
  const pacingStatus = getPacingStatus();
  
  // Calculate adjustment percentage
  const adjustmentPercentage = Math.round((cappedArousalRatio - 1) * 100);
  
  return {
    currentArousal: Math.round(averageArousal * 10) / 10, // Round to 1 decimal
    isHighComplexity,
    optimalHours: Math.round(optimalHours * 10) / 10, // Store as decimal
    optimalHoursFormatted: formatHoursAndMinutes(optimalHours), // Formatted for display
    hoursWorked: hours, // Store the original hours worked for reference
    hoursRange: {
      min: Math.round(minHours * 10) / 10, // Store as decimal
      max: Math.round(maxHours * 10) / 10, // Store as decimal
      minFormatted: formatHoursAndMinutes(minHours), // Formatted for display
      maxFormatted: formatHoursAndMinutes(maxHours) // Formatted for display
    },
    pacingStatus,
    adjustmentPercentage,
    arousalRatio: Math.round(cappedArousalRatio * 100) / 100,
    optimalArousalRange,
    timestamp: Date.now()
  };
};

// Calculate total emotion score for a single entry
const calculateTotalEmotionScore = (emotionalStates) => {
  if (!emotionalStates) return 0;
  const factors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
  const scores = factors.map(factor => emotionalStates[factor] || 0);
  return scores.reduce((sum, score) => sum + score, 0);
};

// Calculate Refresh Rate for today based on trailing mental reserves levels
export const calculateRefreshRate = (journalEntries) => {
  if (!journalEntries || journalEntries.length === 0) return null;
  
  try {
    // Filter and sort entries
    const entries = journalEntries
      .filter(entry => entry.timestamp && entry.emotionalStates)
      .sort((a, b) => {
        const aTime = convertFirebaseTimestamp(a.timestamp) || a.timestamp;
        const bTime = convertFirebaseTimestamp(b.timestamp) || b.timestamp;
        return new Date(aTime) - new Date(bTime);
      });
    
    if (entries.length === 0) return null;
    
    // Use the most recent entry (last index)
    const targetIndex = entries.length - 1;
    
    // Use up to 15 previous entries for calculation (same as Mental Resilience)
    const startIndex = Math.max(0, targetIndex - 14);
    const relevantEntries = entries.slice(startIndex, targetIndex + 1);
    
    // Calculate total Mental Reserves sum for each entry in the window
    const totalReservesSums = relevantEntries
      .map(entry => calculateTotalEmotionScore(entry.emotionalStates))
      .filter(sum => sum > 0); // Filter out entries with no valid data
    
    if (totalReservesSums.length === 0) return null;
    
    // Calculate average of recent total Mental Reserves sums
    const averageTotalReserves = totalReservesSums.reduce((sum, val) => sum + val, 0) / totalReservesSums.length;
    
    // Convert to percentage on scale from 0 to 70
    const refreshRate = (averageTotalReserves / 70) * 100;
    
    // Clamp to 0-100 range and round to 1 decimal place
    return Math.round(Math.max(0, Math.min(100, refreshRate)) * 10) / 10;
  } catch (error) {
    console.error('Error calculating refresh rate:', error);
    return null;
  }
};

// Build the complete funnel flow by following nextScreen links
export const buildFunnelFlow = async (startingKey = 'welcome') => {
  try {
    const [allScreens, allCheckpoints] = await Promise.all([
      getAllScreens(),
      getAllCheckpoints()
    ]);
    
    const allItems = { ...allScreens, ...allCheckpoints };
    const flow = [];
    const visited = new Set();
    
    let currentKey = startingKey;
    
    while (currentKey && !visited.has(currentKey)) {
      const currentItem = allItems[currentKey];
      if (!currentItem) break;
      
      visited.add(currentKey);
      flow.push({
        key: currentKey,
        type: currentItem.type,
        isCheckpoint: !!allCheckpoints[currentKey]
      });
      
      // Get next screen (handling conditional navigation); stop at external URLs
      const nextKey = getNextScreen(currentItem);
      currentKey = isExternalUrl(nextKey) ? null : nextKey;
    }
    
    return flow;
  } catch (error) {
    console.error('Error building funnel flow:', error);
    return [];
  }
};

// Calculate section-based progress for progress bar
export const calculateSectionProgress = async (currentScreenKey, completedScreens = [], userSession = null) => {
  try {
    const flow = await buildFunnelFlow();
    if (!flow.length) return { progress: 0, sectionInfo: null };
    
    // Find current position in flow
    const currentIndex = flow.findIndex(item => item.key === currentScreenKey);
    if (currentIndex === -1) return { progress: 0, sectionInfo: null };
    
    // Find the current section boundaries
    // Section starts from: last completed checkpoint OR start of funnel
    // Section ends at: next upcoming checkpoint OR end of funnel
    
    let sectionStart = 0;
    let sectionEnd = flow.length - 1;
    
    // Find last checkpoint before current position (completed or not)
    for (let i = currentIndex - 1; i >= 0; i--) {
      const item = flow[i];
      if (item.isCheckpoint) {
        sectionStart = i + 1; // Start after the checkpoint
        break;
      }
    }
    
    // Find next upcoming checkpoint after current position
    for (let i = currentIndex + 1; i < flow.length; i++) {
      const item = flow[i];
      if (item.isCheckpoint) {
        sectionEnd = i - 1; // End before the checkpoint
        break;
      }
    }
    
    // Calculate progress within current section
    const sectionLength = sectionEnd - sectionStart + 1;
    const currentPositionInSection = currentIndex - sectionStart;
    
    let progress;
    if (sectionLength <= 1) {
      // Single item in section - show 100% progress
      progress = 100;
    } else if (currentPositionInSection === 0) {
      // First item in section - show 0% progress
      progress = 0;
    } else if (currentPositionInSection >= sectionLength - 1) {
      // Last item in section - show 100% progress
      progress = 100;
    } else {
      // Calculate proportional progress
      progress = Math.round((currentPositionInSection / (sectionLength - 1)) * 100);
    }
    
    const sectionInfo = {
      sectionStart: sectionStart,
      sectionEnd: sectionEnd,
      sectionLength: sectionLength,
      currentPositionInSection: currentPositionInSection,
      startKey: flow[sectionStart]?.key,
      endKey: flow[sectionEnd]?.key,
      nextCheckpointKey: flow[sectionEnd + 1]?.key || null
    };
    
    return { 
      progress: Math.round(progress), 
      sectionInfo 
    };
    
  } catch (error) {
    console.error('Error calculating section progress:', error);
    return { progress: 0, sectionInfo: null };
  }
};

// Process merge fields in text content
export const processMergeFields = (text, userProgress = {}) => {
  if (!text || typeof text !== 'string') return text;
  
  let processedText = text;
  
  // Process shortcode merge - only for the main text description field
  if (processedText.includes('{shortcode}')) {
    const userShortcode = userProgress.shortcode || '';
    processedText = processedText.replace(/\{shortcode\}/g, userShortcode);
  }
  
  // Replace {MentalResilience} with the integer score
  if (processedText.includes('{MentalResilience}')) {
    const mentalResilienceScore = userProgress.calculatedMetrics?.mentalResilience?.score;
    const replacement = mentalResilienceScore !== undefined ? Math.round(mentalResilienceScore).toString() : '[Not calculated yet]';
    processedText = processedText.replace(/\{MentalResilience\}/g, replacement);
  }
  
  // Replace {MentalResilienceClassification} with the classification text
  if (processedText.includes('{MentalResilienceClassification}')) {
    const classification = userProgress.calculatedMetrics?.mentalResilience?.classification;
    const replacement = classification ? classification.toLowerCase() : '[Not calculated yet]';
    processedText = processedText.replace(/\{MentalResilienceClassification\}/g, replacement);
  }
  
  // Replace {MentalResilienceConfidence} with the confidence message
  if (processedText.includes('{MentalResilienceConfidence}')) {
    const confidence = userProgress.calculatedMetrics?.mentalResilience?.confidence?.message;
    const replacement = confidence ? confidence.toLowerCase() : '[Not calculated yet]';
    processedText = processedText.replace(/\{MentalResilienceConfidence\}/g, replacement);
  }
  
  // Replace {OptimalPaceHours} with the formatted hours string
  if (processedText.includes('{OptimalPaceHours}')) {
    const optimalPaceHours = userProgress.calculatedMetrics?.optimalPace?.optimalHoursFormatted;
    const replacement = optimalPaceHours || '[Not calculated yet]';
    processedText = processedText.replace(/\{OptimalPaceHours\}/g, replacement);
  }
  
  // Safe Sprinting merge fields
  // Replace {HoursNeededToWork} with the hours needed to work during sprint
  if (processedText.includes('{HoursNeededToWork}')) {
    const hoursNeeded = userProgress.calculatedMetrics?.safeSprinting?.hoursNeededToWork;
    const replacement = hoursNeeded !== undefined ? hoursNeeded.toString() : '[Not calculated yet]';
    processedText = processedText.replace(/\{HoursNeededToWork\}/g, replacement);
  }
  
  // Replace {DaysOfSprint} with the number of sprint days
  if (processedText.includes('{DaysOfSprint}')) {
    const daysOfSprint = userProgress.calculatedMetrics?.safeSprinting?.daysOfSprint;
    const replacement = daysOfSprint !== undefined ? daysOfSprint.toString() : '[Not calculated yet]';
    processedText = processedText.replace(/\{DaysOfSprint\}/g, replacement);
  }
  
  // Replace {HoursBelowOptimalPace} with the recovery hours below optimal pace
  if (processedText.includes('{HoursBelowOptimalPace}')) {
    const hoursBelow = userProgress.calculatedMetrics?.safeSprinting?.hoursBelowOptimalPace;
    const replacement = hoursBelow !== undefined ? hoursBelow.toString() : '[Not calculated yet]';
    processedText = processedText.replace(/\{HoursBelowOptimalPace\}/g, replacement);
  }
  
  // Replace {AdjustedOptimalPace} with the adjusted optimal pace during recovery
  if (processedText.includes('{AdjustedOptimalPace}')) {
    const adjustedPace = userProgress.calculatedMetrics?.safeSprinting?.adjustedOptimalPace;
    const replacement = adjustedPace !== undefined ? adjustedPace.toString() : '[Not calculated yet]';
    processedText = processedText.replace(/\{AdjustedOptimalPace\}/g, replacement);
  }
  
  // Replace {TotalProductivityDebt} with the total productivity debt in hours
  if (processedText.includes('{TotalProductivityDebt}')) {
    const totalDebt = userProgress.calculatedMetrics?.safeSprinting?.totalProductivityDebt;
    const replacement = totalDebt !== undefined ? totalDebt.toString() : '[Not calculated yet]';
    processedText = processedText.replace(/\{TotalProductivityDebt\}/g, replacement);
  }
  
  // Replace {DaysToRecover} with the number of days needed to recover
  if (processedText.includes('{DaysToRecover}')) {
    const daysToRecover = userProgress.calculatedMetrics?.safeSprinting?.daysToRecover;
    const replacement = daysToRecover !== undefined ? daysToRecover.toString() : '[Not calculated yet]';
    processedText = processedText.replace(/\{DaysToRecover\}/g, replacement);
  }
  
  // Replace {monthlyAmount} with the membership monthly amount
  if (processedText.includes('{monthlyAmount}')) {
    const monthlyAmount = userProgress.membershipSelection?.monthlyAmount;
    const replacement = monthlyAmount !== undefined ? `$${Math.round(monthlyAmount)}` : '[No membership selected]';
    processedText = processedText.replace(/\{monthlyAmount\}/g, replacement);
  }
  
  // Replace {checkpointAmount} with the current checkpoint's dollar amount
  if (processedText.includes('{checkpointAmount}')) {
    const checkpointAmount = userProgress.currentCheckpoint?.content?.dollarAmount;
    const replacement = checkpointAmount !== undefined ? `$${checkpointAmount.toFixed(2)}` : '[Amount not set]';
    processedText = processedText.replace(/\{checkpointAmount\}/g, replacement);
  }
  
  // Support for new double-brace format for backward compatibility
  // {{name}} - User's name
  if (userProgress.userInfo?.name) {
    processedText = processedText.replace(/\{\{name\}\}/g, userProgress.userInfo.name);
  }
  
  // {{email}} - User's email
  if (userProgress.userInfo?.email) {
    processedText = processedText.replace(/\{\{email\}\}/g, userProgress.userInfo.email);
  }
  
  // {{optimalPace}} - User's calculated optimal pace (formatted)
  if (userProgress.calculatedMetrics?.optimalPace) {
    const pace = userProgress.calculatedMetrics.optimalPace;
    const hoursDisplay = pace.hours > 0 ? `${pace.hours} hour${pace.hours !== 1 ? 's' : ''}` : '';
    const minutesDisplay = pace.minutes > 0 ? `${pace.minutes} minute${pace.minutes !== 1 ? 's' : ''}` : '';
    
    let paceDisplay = '';
    if (hoursDisplay && minutesDisplay) {
      paceDisplay = `${hoursDisplay} and ${minutesDisplay}`;
    } else if (hoursDisplay) {
      paceDisplay = hoursDisplay;
    } else if (minutesDisplay) {
      paceDisplay = minutesDisplay;
    } else {
      paceDisplay = 'no time';
    }
    
    processedText = processedText.replace(/\{\{optimalPace\}\}/g, paceDisplay);
  }
  
  return processedText;
};

// Format text content for display - converts newlines to HTML and supports basic HTML tags
export const formatTextContent = (text, userProgress = {}) => {
  if (!text || typeof text !== 'string') return null;
  
  // Process merge fields first
  let processedText = processMergeFields(text, userProgress);
  
  // Convert single newlines to <br/> tags for line breaks
  processedText = processedText.replace(/\n/g, '<br/>');

  // Style inline <a> links so they stand out in screen text fields
  processedText = processedText.replace(/<a\b([^>]*)>/gi, (match, attrs) => {
    if (/\bclass\s*=/i.test(attrs)) {
      return match.replace(/\bclass\s*=\s*(["'])([^"']*)\1/i, (classMatch, quote, classes) => {
        if (classes.split(/\s+/).includes('screen-text-link')) return classMatch;
        return `class=${quote}${classes} screen-text-link${quote}`;
      });
    }
    return `<a class="screen-text-link"${attrs}>`;
  });
  
  // Return the HTML content (React will need to use dangerouslySetInnerHTML)
  return processedText;
};

// Calculate mental resilience based on journal entry data
export const calculateMentalResilience = (journalData) => {
  try {
    // Get all journal entries for the user to calculate resilience
    // This function is called from saveJournalEntry, so we need to get the user's full history
    // For now, we'll return a placeholder - the real calculation will happen in saveJournalEntry
    // when we have access to the full user data
    
    return {
      score: null,
      error: "Calculation requires full user journal history",
      timestamp: Date.now()
    };
  } catch (error) {
    console.error('Error calculating mental resilience:', error);
    return {
      score: null,
      error: error.message,
      timestamp: Date.now()
    };
  }
};

// Calculate mental resilience with full user data
export const calculateMentalResilienceWithHistory = (journalEntries) => {
  try {
    // Filter entries with valid pressure values (0-10) and at least one emotional factor
    const validEntries = journalEntries.filter(entry => {
      const hasValidPressure = entry.pressureScore !== null && 
                              entry.pressureScore !== undefined && 
                              entry.pressureScore >= 0 && 
                              entry.pressureScore <= 10;
      
      const hasEmotionalData = entry.emotionalStates && 
                              Object.values(entry.emotionalStates).some(val => 
                                val !== null && val !== undefined && val >= 0 && val <= 10
                              );
      
      return hasValidPressure && hasEmotionalData;
    });

    if (validEntries.length < 2) {
      // Use Bayesian prediction for users with insufficient data
      return calculateBayesianMentalResilience(validEntries);
    }

    // Check if pressure varies (required for regression analysis)
    const pressureValues = validEntries.map(entry => entry.pressureScore);
    const uniquePressures = [...new Set(pressureValues)];
    
    if (uniquePressures.length < 2) {
      return {
        score: null,
        error: "No pressure variation - need entries with different pressure levels",
        confidence: {
          level: "insufficient",
          message: "Need entries with different pressure levels to analyze resilience",
          recommendations: [
            "Include entries from both low and high pressure situations",
            "Rate pressure honestly relative to your experience",
            "Vary your journaling to include different stress levels"
          ]
        },
        timestamp: Date.now()
      };
    }

    // Calculate aggregated emotional scores for each entry
    const dataPoints = validEntries.map(entry => {
      const emotionalFactors = [
        entry.emotionalStates.hours,      // Personal Identity
        entry.emotionalStates.focus,      // Focus
        entry.emotionalStates.calm,       // Calm
        entry.emotionalStates.happiness,  // Positivity
        entry.emotionalStates.depression, // Satisfaction
        entry.emotionalStates.mania,      // Control
        entry.emotionalStates.energy      // Energy
      ].filter(val => val !== null && val !== undefined && val >= 0 && val <= 10);
      
      const aggregatedEmotionalScore = emotionalFactors.length > 0 
        ? emotionalFactors.reduce((sum, val) => sum + val, 0) / emotionalFactors.length
        : 5; // Fallback if no emotional data
      
      return [entry.pressureScore, aggregatedEmotionalScore];
    });

    // Perform linear regression
    const regression = linearRegression(dataPoints);
    const { slope, intercept, rSquared, standardError, pValue } = regression;

    // Calculate Mental Resilience Score
    // Score = max(0, min(100, 85 + (slope * 50)))
    const rawScore = 85 + (slope * 50);
    const score = Math.max(0, Math.min(100, Math.round(rawScore)));

    // Determine confidence level based on p-value
    let confidenceLevel, confidenceMessage;
    if (pValue <= 0.01) {
      confidenceLevel = "very_high";
      confidenceMessage = "is very confident in this score";
    } else if (pValue <= 0.05) {
      confidenceLevel = "high";
      confidenceMessage = "is confident in this score";
    } else if (pValue <= 0.10) {
      confidenceLevel = "moderate";
      confidenceMessage = "is moderately confident in this score";
    } else if (pValue <= 0.20) {
      confidenceLevel = "low";
      confidenceMessage = "has confidence in this score";
    } else {
      confidenceLevel = "very_low";
      confidenceMessage = "has very low confidence in this score";
    }

    // Determine sample size adequacy
    let sampleSizeAdequacy;
    if (validEntries.length >= 30) {
      sampleSizeAdequacy = "excellent";
    } else if (validEntries.length >= 15) {
      sampleSizeAdequacy = "good";
    } else if (validEntries.length >= 8) {
      sampleSizeAdequacy = "adequate";
    } else if (validEntries.length >= 5) {
      sampleSizeAdequacy = "minimal";
    } else {
      sampleSizeAdequacy = "insufficient";
    }

    // Determine interpretation based on score
    let interpretation;
    if (score > 85) {
      interpretation = "increases under pressure";
    } else if (score == 85) {
      interpretation = "holds steady under pressure";
    } else if (score >= 70) {
      interpretation = "falls slightly under pressure";
    } else if (score >= 50) {
      interpretation = "falls moderately under pressure";
    } else if (score >= 35) {
      interpretation = "falls significantly under pressure";
    } else {
      interpretation = "crashes under pressure";
    }

    // Determine superhuman classification
    const isSuperhuman = score >= 85;
    const classification = score > 85 ? 'increases under pressure' :
                         score == 85 ? 'holds steady under pressure' : 
                         score >= 70 ? 'falls slightly under pressure' :
                         score >= 50 ? 'falls moderately under pressure' : 
                         score >= 35 ? 'falls significantly under pressure' : 
                         'crashes under pressure';

    // Generate recommendations based on score and confidence
    const recommendations = generateRecommendations(score, confidenceLevel, validEntries.length, isSuperhuman);

    return {
      score,
      slope: Math.round(slope * 1000) / 1000,
      intercept: Math.round(intercept * 1000) / 1000,
      rSquared: Math.round(rSquared * 1000) / 1000,
      interpretation,
      classification,
      isSuperhuman,
      confidence: {
        level: confidenceLevel,
        message: confidenceMessage,
        statistical: {
          pValue: Math.round(pValue * 1000) / 1000,
          significanceLevel: pValue <= 0.05 ? "significant" : "not_significant",
          sampleSizeAdequacy,
          entriesAnalyzed: validEntries.length
        }
      },
      recommendations,
      timestamp: Date.now()
    };

  } catch (error) {
    console.error('Error calculating mental resilience:', error);
    return {
      score: null,
      error: error.message,
      confidence: {
        level: "error",
        message: "Error occurred during calculation",
        recommendations: [
          "Please try again with your next journal entry",
          "Ensure all data is properly filled out",
          "Contact support if the issue persists"
        ]
      },
      timestamp: Date.now()
    };
  }
};

// Linear regression calculation
function linearRegression(dataPoints) {
  const n = dataPoints.length;
  const sumX = dataPoints.reduce((sum, [x]) => sum + x, 0);
  const sumY = dataPoints.reduce((sum, [x, y]) => sum + y, 0);
  const sumXY = dataPoints.reduce((sum, [x, y]) => sum + x * y, 0);
  const sumXX = dataPoints.reduce((sum, [x]) => sum + x * x, 0);
  const sumYY = dataPoints.reduce((sum, [x, y]) => sum + y * y, 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Calculate R-squared
  const yMean = sumY / n;
  const ssRes = dataPoints.reduce((sum, [x, y]) => {
    const predicted = slope * x + intercept;
    return sum + Math.pow(y - predicted, 2);
  }, 0);
  
  const ssTot = dataPoints.reduce((sum, [x, y]) => {
    return sum + Math.pow(y - yMean, 2);
  }, 0);
  
  const rSquared = ssTot > 0 ? 1 - (ssRes / ssTot) : 0;
  
  // Calculate standard error of slope
  const standardError = Math.sqrt(ssRes / (n - 2) / (sumXX - (sumX * sumX) / n));
  
  // Calculate p-value using t-test
  const tStatistic = Math.abs(slope / standardError);
  const degreesOfFreedom = n - 2;
  
  // P-value estimation (simplified)
  let pValue;
  if (degreesOfFreedom >= 30) {
    // Normal approximation for large samples
    pValue = 2 * (1 - cumulativeStdNormalProbability(tStatistic));
  } else {
    // Conservative estimate for small samples
    pValue = degreesOfFreedom <= 2 ? 0.5 : 0.1;
  }
  
  return { slope, intercept, rSquared, standardError, pValue };
}

// Cumulative standard normal probability (approximation)
function cumulativeStdNormalProbability(z) {
  // Simple approximation using error function
  return 0.5 * (1 + erf(z / Math.sqrt(2)));
}

// Error function approximation
function erf(x) {
  const a1 =  0.254829592;
  const a2 = -0.284496736;
  const a3 =  1.421413741;
  const a4 = -1.453152027;
  const a5 =  1.061405429;
  const p  =  0.3275911;
  
  const sign = x >= 0 ? 1 : -1;
  x = Math.abs(x);
  
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  
  return sign * y;
}

// Generate recommendations based on score and confidence
function generateRecommendations(score, confidenceLevel, entryCount, isSuperhuman) {
  const recommendations = [];
  
  if (entryCount < 3) {
    recommendations.push(
      "Focus on quality: Accurate pressure and emotion ratings",
      "Seek variety: Include different pressure situations",
      "Daily journaling: Build consistent measurement habits",
      "Honest assessment: Rate emotions relative to pressure honestly"
    );
  } else if (entryCount < 15) {
    recommendations.push(
      "Pattern recognition: Look for consistent pressure-emotion relationships",
      "Statistical foundation: Build toward statistical significance",
      "Diverse scenarios: Include work, personal, and low-pressure situations",
      "Regular assessment: Maintain consistent journaling schedule"
    );
  } else {
    recommendations.push(
      "High-confidence insights: Reliable personal pattern analysis",
      "Trend monitoring: Track resilience changes over time",
      "Intervention testing: Measure effectiveness of stress management",
      "Long-term patterns: Identify seasonal or life-cycle variations"
    );
  }
  
  if (isSuperhuman) {
    recommendations.push(
      "Status maintenance: Monitor consistency of superhuman patterns",
      "Performance optimization: Identify optimal pressure levels for peak performance",
      "Leadership development: Leverage resilience patterns for team support",
      "Mentorship potential: Share strategies with users seeking improvement"
    );
  } else if (score < 50) {
    recommendations.push(
      "Stress management: Practice emotional regulation techniques",
      "Pressure exposure: Gradually increase exposure to manageable pressure",
      "Coping strategies: Develop healthy stress response mechanisms",
      "Professional support: Consider consulting with mental health professionals"
    );
  }
  
  if (confidenceLevel === "low" || confidenceLevel === "very_low") {
    recommendations.push(
      "Increase data quality: Ensure consistent rating scales",
      "More entries: Continue journaling to improve statistical significance",
      "Pressure variation: Include entries from different stress levels"
    );
  }
  
  return recommendations;
}

// Bayesian prediction for users with insufficient data
function calculateBayesianMentalResilience(validEntries) {
  try {
    // Population priors from dataset analysis of 1,756 users
    const populationPriors = {
      // Pressure-based resilience distributions
      pressureBased: {
        low: { mean: 48.2, stdDev: 18.5, alpha: 3.2, beta: 4.1, weight: 0.40 },
        moderate: { mean: 52.8, stdDev: 16.2, alpha: 4.7, beta: 4.3, weight: 0.40 },
        high: { mean: 58.3, stdDev: 19.7, alpha: 5.8, beta: 3.2, weight: 0.40 }
      },
      // Emotional baseline distributions
      emotionalBaseline: {
        low: { mean: 41.7, stdDev: 20.1, alpha: 2.1, beta: 5.2, weight: 0.30 },
        moderate: { mean: 53.2, stdDev: 15.8, alpha: 4.5, beta: 4.5, weight: 0.30 },
        high: { mean: 64.1, stdDev: 17.3, alpha: 6.1, beta: 2.9, weight: 0.30 }
      },
      // Resilience archetype distributions
      archetypes: {
        pressureSensitive: { mean: 32.4, stdDev: 12.8, alpha: 1.8, beta: 6.7, weight: 0.20 },
        steadyModerate: { mean: 54.7, stdDev: 11.2, alpha: 5.2, beta: 4.1, weight: 0.20 },
        pressureThrivers: { mean: 78.9, stdDev: 14.6, alpha: 7.3, beta: 2.1, weight: 0.20 }
      },
      // General population baseline
      general: { mean: 52.0, stdDev: 16.0, alpha: 4.8, beta: 4.2, weight: 0.10 }
    };

    // Determine user characteristics based on available data
    let userCharacteristics = {
      pressureLevel: 'moderate', // Default
      emotionalBaseline: 'moderate', // Default
      predictedArchetype: 'steadyModerate' // Default
    };

    if (validEntries.length === 1) {
      const entry = validEntries[0];
      
      // Determine pressure level
      if (entry.pressureScore <= 3) {
        userCharacteristics.pressureLevel = 'low';
      } else if (entry.pressureScore >= 7) {
        userCharacteristics.pressureLevel = 'high';
      }
      
      // Determine emotional baseline
      if (entry.emotionalStates) {
        const emotionalValues = Object.values(entry.emotionalStates)
          .filter(val => val !== null && val !== undefined && val >= 0 && val <= 10);
        
        if (emotionalValues.length > 0) {
          const avgEmotional = emotionalValues.reduce((sum, val) => sum + val, 0) / emotionalValues.length;
          
          if (avgEmotional < 4.0) {
            userCharacteristics.emotionalBaseline = 'low';
          } else if (avgEmotional > 6.5) {
            userCharacteristics.emotionalBaseline = 'high';
          }
        }
      }
      
      // Predict archetype based on pressure-emotion relationship
      // For single entry, use conservative estimate
      userCharacteristics.predictedArchetype = 'steadyModerate';
    }

    // Calculate Bayesian estimate
    const pressurePrior = populationPriors.pressureBased[userCharacteristics.pressureLevel];
    const emotionalPrior = populationPriors.emotionalBaseline[userCharacteristics.emotionalBaseline];
    const archetypePrior = populationPriors.archetypes[userCharacteristics.predictedArchetype];
    const generalPrior = populationPriors.general;

    // Weighted combination of priors
    const bayesianScore = (
      pressurePrior.mean * pressurePrior.weight +
      emotionalPrior.mean * emotionalPrior.weight +
      archetypePrior.mean * archetypePrior.weight +
      generalPrior.mean * generalPrior.weight
    );

    // Calculate confidence based on data availability
    const dataWeight = validEntries.length === 1 ? 0.15 : 0.05; // 15% for 1 entry, 5% for 0 entries
    const priorWeight = 1 - dataWeight;

    // If we have some data, incorporate it
    let finalScore = bayesianScore;
    if (validEntries.length === 1) {
      const entry = validEntries[0];
      const dataEstimate = calculateSingleEntryEstimate(entry);
      finalScore = (bayesianScore * priorWeight) + (dataEstimate * dataWeight);
    }

    // Round to whole integer
    finalScore = Math.round(finalScore);

    // Determine classification
    const classification = finalScore >= 85 ? 'is stable under pressure' : 
                         finalScore >= 50 ? 'is moderately affected by pressure' :
                         finalScore >= 25 ? 'notably decreases with pressure' : 
                         'crashes under pressure';

    const isSuperhuman = finalScore >= 85;

    return {
      score: finalScore,
      slope: null, // Not available with Bayesian estimation
      intercept: null,
      rSquared: null,
      interpretation: `Bayesian estimate based on population patterns and ${validEntries.length} journal entry`,
      classification,
      isSuperhuman,
      confidence: {
        level: "bayesian",
        message: `Estimated score based on population patterns (${validEntries.length} entry available)`,
        statistical: {
          pValue: null,
          significanceLevel: "not_applicable",
          sampleSizeAdequacy: "insufficient",
          entriesAnalyzed: validEntries.length,
          estimationMethod: "bayesian"
        }
      },
      recommendations: generateRecommendations(finalScore, "bayesian", validEntries.length, isSuperhuman),
      bayesianAdjustment: {
        priorWeight,
        dataWeight,
        estimateReliability: validEntries.length === 1 ? "moderate" : "low"
      },
      timestamp: Date.now()
    };

  } catch (error) {
    console.error('Error in Bayesian calculation:', error);
    return {
      score: null,
      error: "Error in Bayesian estimation",
      confidence: {
        level: "error",
        message: "Error occurred during Bayesian estimation",
        recommendations: [
          "Please try again with your next journal entry",
          "Ensure all data is properly filled out"
        ]
      },
      timestamp: Date.now()
    };
  }
}

// Calculate estimate from single journal entry
function calculateSingleEntryEstimate(entry) {
  try {
    // Simple heuristic based on pressure-emotion relationship
    if (!entry.emotionalStates) return 50; // Default if no emotional data
    
    const emotionalValues = Object.values(entry.emotionalStates)
      .filter(val => val !== null && val !== undefined && val >= 0 && val <= 10);
    
    if (emotionalValues.length === 0) return 50;
    
    const avgEmotional = emotionalValues.reduce((sum, val) => sum + val, 0) / emotionalValues.length;
    
    // Simple heuristic: if emotions are high relative to pressure, suggest higher resilience
    // This is a simplified version of the full calculation
    let estimate = 50; // Base score
    
    if (entry.pressureScore >= 7) {
      // High pressure situation
      if (avgEmotional >= 7) estimate += 20; // Thriving under pressure
      else if (avgEmotional >= 5) estimate += 5; // Managing under pressure
      else estimate -= 15; // Struggling under pressure
    } else if (entry.pressureScore >= 4) {
      // Moderate pressure situation
      if (avgEmotional >= 7) estimate += 15;
      else if (avgEmotional >= 5) estimate += 0; // Neutral
      else estimate -= 10;
    } else {
      // Low pressure situation
      if (avgEmotional >= 7) estimate += 10;
      else if (avgEmotional >= 5) estimate += 0; // Neutral
      else estimate -= 5;
    }
    
    return Math.max(0, Math.min(100, estimate));
    
  } catch (error) {
    console.error('Error calculating single entry estimate:', error);
    return 50; // Default fallback
  }
}

// Admin Functions
// Save or update a screen
export const saveScreen = async (screenData) => {
  try {
    console.log('saveScreen called with screenData:', screenData);
    console.log('saveScreen - screenData.id:', screenData.id);
    console.log('saveScreen - isNewScreen:', !screenData.id);
    console.log('saveScreen - screenData.key:', screenData.key);
    
    let screenRef;
    
    if (screenData.id) {
      console.log('saveScreen - UPDATING existing screen with ID:', screenData.id);
      // Updating existing screen - use the existing document ID
      screenRef = doc(db, 'screens', screenData.id);
    } else {
      console.log('saveScreen - CREATING new screen (no ID provided)');
      
      // Check if a screen with this key already exists
      const existingScreen = await getScreenData(screenData.key);
      if (existingScreen) {
        console.log('saveScreen - WARNING: Screen with key already exists:', existingScreen);
        throw new Error(`A screen with the key "${screenData.key}" already exists. Please use a unique key.`);
      }
      
      // Creating new screen - let Firestore auto-generate document ID
      screenRef = doc(collection(db, 'screens'));
    }
    
    const dataToSave = {
      ...screenData,
      updatedAt: new Date()
    };
    
    // Remove the id field from the data being saved to Firestore
    delete dataToSave.id;
    
    if (screenData.id) {
      // For existing documents, use updateDoc to ensure we're updating, not creating
      console.log('saveScreen - using updateDoc for existing screen');
      await updateDoc(screenRef, dataToSave);
    } else {
      // For new documents, use setDoc
      console.log('saveScreen - using setDoc for new screen');
      await setDoc(screenRef, dataToSave);
    }
    
    console.log('saveScreen - operation completed, returning ID:', screenRef.id);
    return { id: screenRef.id, ...dataToSave };
  } catch (error) {
    console.error('Error saving screen:', error);
    throw error;
  }
};

// Save or update a checkpoint
export const saveCheckpoint = async (checkpointData) => {
  try {
    let checkpointRef;
    
    if (checkpointData.id) {
      // Updating existing checkpoint - use the existing document ID
      checkpointRef = doc(db, 'checkpoints', checkpointData.id);
    } else {
      // Creating new checkpoint - let Firestore auto-generate document ID
      checkpointRef = doc(collection(db, 'checkpoints'));
    }
    
    const dataToSave = {
      ...checkpointData,
      updatedAt: new Date()
    };
    
    // Remove the id field from the data being saved to Firestore
    delete dataToSave.id;
    
    await setDoc(checkpointRef, dataToSave, { merge: true });
    return { id: checkpointRef.id, ...dataToSave };
  } catch (error) {
    console.error('Error saving checkpoint:', error);
    throw error;
  }
};

// Delete a screen
export const deleteScreen = async (screenId) => {
  try {
    await deleteDoc(doc(db, 'screens', screenId));
  } catch (error) {
    console.error('Error deleting screen:', error);
    throw error;
  }
};

// Delete a checkpoint
export const deleteCheckpoint = async (checkpointId) => {
  try {
    await deleteDoc(doc(db, 'checkpoints', checkpointId));
  } catch (error) {
    console.error('Error deleting checkpoint:', error);
    throw error;
  }
};

/**
 * Initial (backfill) sync of Insatiable journal entries to founders-first / Dory.
 * Calls syncJournalEntriesToDory with the full entries list, then sets users/{shortcode}.synced.
 * Used by saveJournalEntry on first sync and by the admin Sync screen.
 */
export const runInitialJournalSyncToDory = async ({ shortcode, appUserId, entries }) => {
  if (!shortcode) {
    throw new Error('shortcode is required');
  }
  if (!appUserId) {
    throw new Error('appUserId is required');
  }
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new Error('entries must be a non-empty array');
  }
  if (!functions) {
    throw new Error('Firebase Functions is not initialized');
  }

  const { httpsCallable } = await import('firebase/functions');
  const syncJournalEntriesToDory = httpsCallable(functions, 'syncJournalEntriesToDory');

  const result = await syncJournalEntriesToDory({
    shortcode,
    appUserId,
    entries
  });

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const syncedDate = `${yyyy}-${mm}-${dd}`;

  const userRef = doc(db, 'users', shortcode);
  await updateDoc(userRef, {
    synced: syncedDate,
    lastActive: new Date()
  });

  return {
    syncedDate,
    createdCount: result?.data?.createdCount ?? entries.length,
    createdDocIds: result?.data?.createdDocIds || []
  };
};

// Analytics functions
export const getAllUsers = async () => {
  try {
    console.log('Fetching all users from Firebase...');
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const users = [];
    
    snapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    console.log(`Found ${users.length} users`);
    return users;
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Split Insatiable users into pending (ready for initial Dory sync) and already-synced lists.
 * Pending: appUserId + no synced flag + at least one journal entry.
 * Synced: appUserId + synced flag set.
 */
export const getDorySyncLists = async () => {
  const users = await getAllUsers();

  const pending = [];
  const synced = [];

  users.forEach((user) => {
    const shortcode = user.shortcode || user.id;
    if (!user.appUserId || !shortcode) return;

    const mapped = {
      ...user,
      shortcode,
      journalEntryCount: (user.journalEntries || []).length
    };

    if (user.synced) {
      synced.push(mapped);
    } else if ((user.journalEntries || []).length > 0) {
      pending.push(mapped);
    }
  });

  pending.sort((a, b) => {
    const aTime = a.lastActive ? new Date(convertFirebaseTimestamp(a.lastActive) || a.lastActive).getTime() : 0;
    const bTime = b.lastActive ? new Date(convertFirebaseTimestamp(b.lastActive) || b.lastActive).getTime() : 0;
    return bTime - aTime;
  });

  synced.sort((a, b) => {
    const aSynced = typeof a.synced === 'string' ? a.synced : '';
    const bSynced = typeof b.synced === 'string' ? b.synced : '';
    if (aSynced !== bSynced) return bSynced.localeCompare(aSynced);
    return (a.shortcode || '').localeCompare(b.shortcode || '');
  });

  return { pending, synced };
};

export const getDailyAnalytics = async (days = 30) => {
  try {
    console.log('Starting daily analytics calculation...');
    const users = await getAllUsers();
    
    // Filter out localhost/testing sessions FIRST - analytics should never show localhost data
    // Backwards compatible: users without hostname are treated as production users
    const productionUsers = users.filter(user => {
      const hostname = user.hostname || '';
      
      // If no hostname field exists (backwards compatibility), treat as production
      if (!user.hasOwnProperty('hostname')) {
        return true;
      }
      
      // If hostname is empty or localhost-related, exclude
      return !hostname.includes('localhost') && 
             !hostname.includes('127.0.0.1') && 
             !hostname.includes('0.0.0.0') &&
             hostname !== '';
    });
    
    // Debug: Count users by hostname status
    const usersWithoutHostname = users.filter(user => !user.hasOwnProperty('hostname'));
    const usersWithHostname = users.filter(user => user.hasOwnProperty('hostname'));
    const excludedLocalhostUsers = users.filter(user => {
      const hostname = user.hostname || '';
      return user.hasOwnProperty('hostname') && (
        hostname.includes('localhost') || 
        hostname.includes('127.0.0.1') || 
        hostname.includes('0.0.0.0') ||
        hostname === ''
      );
    });
    
    console.log(`Total users: ${users.length}, Production users: ${productionUsers.length}`);
    console.log(`- Users without hostname field (legacy): ${usersWithoutHostname.length}`);
    console.log(`- Users with hostname field: ${usersWithHostname.length}`);
    console.log(`- Localhost users (excluded): ${excludedLocalhostUsers.length}`);
    
    // If no production users, return empty analytics
    if (productionUsers.length === 0) {
      console.log('No production users found, returning empty analytics');
      return {};
    }
    
    const analytics = {};
    
    // Initialize analytics for the last N days (including today)
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      analytics[dateKey] = {
        date: dateKey,
        funnelStarts: 0,
        totalScreenVisits: 0,
        averageScreenVisits: 0,
        users: []
      };
    }
    
    // Debug: Log the date range being analyzed
    const dateKeys = Object.keys(analytics).sort();
    console.log(`Analytics date range: ${dateKeys[0]} to ${dateKeys[dateKeys.length - 1]} (${dateKeys.length} days)`);
    console.log(`Current date/time: ${new Date().toISOString()}`);
    console.log(`Today's date key should be: ${new Date().toISOString().split('T')[0]}`);
    console.log(`Local time: ${new Date().toString()}`);
    console.log(`Local date key: ${new Date().toLocaleDateString('en-CA')}`);
    
    // Debug: Log sample production user data
    console.log('Sample production user data:', productionUsers.slice(0, 3).map(user => ({
      shortcode: user.shortcode,
      startTime: user.startTime,
      startTimeType: typeof user.startTime,
      hostname: user.hostname,
      completedScreens: user.completedScreens?.length || 0,
      currentScreen: user.currentScreen
    })));
    
    // Debug: Check for invalid startTime values in production users
    const invalidUsers = productionUsers.filter(user => {
      if (!user.startTime) return false;
      const date = convertFirebaseTimestamp(user.startTime);
      return !date || isNaN(date.getTime());
    });
    
    if (invalidUsers.length > 0) {
      console.warn(`Found ${invalidUsers.length} production users with invalid startTime values:`, 
        invalidUsers.map(u => ({ shortcode: u.shortcode, startTime: u.startTime }))
      );
    }
    
    // Process each production user (excluding localhost)
    let processedCount = 0;
    let skippedCount = 0;
    
    productionUsers.forEach((user, index) => {
      if (user.startTime) {
        try {
          const startDate = convertFirebaseTimestamp(user.startTime);
          
          // Check if the date is valid
          if (!startDate || isNaN(startDate.getTime())) {
            console.warn(`Invalid startTime for user ${user.shortcode}:`, user.startTime);
            skippedCount++;
            return; // Skip this user
          }
          
          const dateKey = startDate.toISOString().split('T')[0];
          
          // Debug: Log users being processed for today specifically
          const todayKey = new Date().toISOString().split('T')[0];
          if (dateKey === todayKey) {
            console.log(`Processing today's user ${user.shortcode}: startTime=${user.startTime}, convertedDate=${startDate.toISOString()}, dateKey=${dateKey}`);
          }
          
          if (analytics[dateKey]) {
            analytics[dateKey].funnelStarts++;
            
            // Calculate screen visits (completed screens + current screen if exists)
            const screenVisits = (user.completedScreens?.length || 0) + 
                                (user.currentScreen ? 1 : 0);
            
            analytics[dateKey].totalScreenVisits += screenVisits;
            analytics[dateKey].users.push({
              shortcode: user.shortcode,
              screenVisits: screenVisits,
              completedScreens: user.completedScreens?.length || 0,
              currentScreen: user.currentScreen
            });
            
            processedCount++;
          } else {
            console.log(`Skipping user ${user.shortcode}: date ${dateKey} not in analytics range`);
            // Debug: Show what dates are available in analytics
            const availableDates = Object.keys(analytics).sort();
            console.log(`Available dates in analytics: ${availableDates.join(', ')}`);
            skippedCount++;
          }
        } catch (dateError) {
          console.warn(`Error processing startTime for user ${user.shortcode}:`, dateError);
          skippedCount++;
          // Continue processing other users
        }
      } else {
        skippedCount++;
        console.warn(`User ${user.shortcode} has no startTime`);
      }
      
      if (index % 10 === 0) {
        console.log(`Processed ${index + 1}/${productionUsers.length} production users`);
      }
    });
    
    console.log(`Processing complete: ${processedCount} production users processed, ${skippedCount} skipped`);
    
    // Debug: Show today's data specifically
    const todayKey = new Date().toISOString().split('T')[0];
    const todayData = analytics[todayKey];
    if (todayData) {
      console.log(`Today's data (${todayKey}): ${todayData.funnelStarts} funnel starts, ${todayData.totalScreenVisits} total visits, ${todayData.averageScreenVisits} avg visits per user`);
    } else {
      console.log(`No data found for today (${todayKey})`);
    }
    
    // Debug: Show the final analytics structure
    console.log('Final analytics structure:', Object.keys(analytics).sort().map(dateKey => ({
      date: dateKey,
      funnelStarts: analytics[dateKey].funnelStarts,
      totalScreenVisits: analytics[dateKey].totalScreenVisits,
      averageScreenVisits: analytics[dateKey].averageScreenVisits
    })));
    
    // Calculate averages
    Object.keys(analytics).forEach(dateKey => {
      const dayData = analytics[dateKey];
      if (dayData.funnelStarts > 0) {
        dayData.averageScreenVisits = Math.round(
          (dayData.totalScreenVisits / dayData.funnelStarts) * 100
        ) / 100; // Round to 2 decimal places
      }
    });
    
    // Debug: Log analytics summary
    const totalStarts = Object.values(analytics).reduce((sum, day) => sum + day.funnelStarts, 0);
    const totalVisits = Object.values(analytics).reduce((sum, day) => sum + day.totalScreenVisits, 0);
    const daysWithData = Object.values(analytics).filter(day => day.funnelStarts > 0).length;
    
    const totalExcludedUsers = users.length - productionUsers.length;
    
    console.log('Analytics summary:', {
      totalUsersInDatabase: users.length,
      productionUsersAnalyzed: productionUsers.length,
      usersExcludedFromAnalytics: totalExcludedUsers,
      processedUsers: processedCount,
      skippedUsers: skippedCount,
      totalFunnelStarts: totalStarts,
      totalScreenVisits: totalVisits,
      daysWithData: daysWithData,
      averageVisitsPerUser: totalStarts > 0 ? Math.round((totalVisits / totalStarts) * 100) / 100 : 0
    });
    
    console.log('Analytics calculation completed successfully');
    return analytics;
  } catch (error) {
    console.error('Error getting daily analytics:', error);
    throw error;
  }
};

// Helper function to clean up invalid date data
export const cleanupInvalidDates = async () => {
  try {
    console.log('Starting cleanup of invalid date data...');
    const users = await getAllUsers();
    const invalidUsers = users.filter(user => {
      if (!user.startTime) return false;
      const date = convertFirebaseTimestamp(user.startTime);
      return !date || isNaN(date.getTime());
    });
    
    console.log(`Found ${invalidUsers.length} users with invalid startTime values`);
    
    for (const user of invalidUsers) {
      try {
        // Update the user with a valid startTime (current time)
        const userRef = doc(db, 'users', user.shortcode);
        await updateDoc(userRef, {
          startTime: new Date(),
          lastActive: new Date()
        });
        console.log(`Fixed startTime for user ${user.shortcode}`);
      } catch (updateError) {
        console.error(`Failed to fix user ${user.shortcode}:`, updateError);
      }
    }
    
    console.log('Cleanup completed');
    return { fixed: invalidUsers.length };
  } catch (error) {
    console.error('Error during cleanup:', error);
    throw error;
  }
};

// Get screen completion frequency analytics
export const getScreenCompletionFrequency = async () => {
  try {
    console.log('Starting screen completion frequency calculation...');
    const users = await getAllUsers();
    
    // Filter out localhost/testing sessions - analytics should never show localhost data
    const productionUsers = users.filter(user => {
      const hostname = user.hostname || '';
      
      // If no hostname field exists (backwards compatibility), treat as production
      if (!user.hasOwnProperty('hostname')) {
        return true;
      }
      
      // If hostname is empty or localhost-related, exclude
      return !hostname.includes('localhost') && 
             !hostname.includes('127.0.0.1') && 
             !hostname.includes('0.0.0.0') &&
             hostname !== '';
    });
    
    console.log(`Total users: ${users.length}, Production users: ${productionUsers.length}`);
    
    // If no production users, return empty analytics
    if (productionUsers.length === 0) {
      console.log('No production users found, returning empty screen completion frequency');
      return {};
    }
    
    // Count users by number of completed screens
    const completionFrequency = {};
    
    productionUsers.forEach(user => {
      const completedScreens = user.completedScreens?.length || 0;
      
      if (!completionFrequency[completedScreens]) {
        completionFrequency[completedScreens] = 0;
      }
      completionFrequency[completedScreens]++;
    });
    
    console.log('Screen completion frequency:', completionFrequency);
    
    return completionFrequency;
  } catch (error) {
    console.error('Error getting screen completion frequency:', error);
    throw error;
  }
};

const isProductionHostname = (user) => {
  // Backwards compatible: users without hostname are treated as production
  if (!Object.prototype.hasOwnProperty.call(user, 'hostname')) {
    return true;
  }
  const hostname = user.hostname || '';
  return (
    hostname !== '' &&
    !hostname.includes('localhost') &&
    !hostname.includes('127.0.0.1') &&
    !hostname.includes('0.0.0.0')
  );
};

const attributionPlatformsFrom = (attribution = {}) => {
  const platforms = [];
  if (attribution.fbclid) platforms.push('meta');
  if (attribution.gclid || attribution.gbraid || attribution.wbraid) {
    platforms.push('google');
  }
  if (attribution.li_fat_id) platforms.push('linkedin');
  return platforms;
};

const summarizeConversionEvents = (events = []) => {
  const summary = {
    total: events.length,
    metaSent: 0,
    linkedinSent: 0,
    googleSent: 0,
    anySent: 0,
    errors: 0
  };

  events.forEach((event) => {
    const results = event?.results || {};
    let eventSent = false;
    ['meta', 'linkedin', 'google'].forEach((platform) => {
      const result = results[platform] || {};
      if (result.sent || result.validated) {
        eventSent = true;
        if (platform === 'meta') summary.metaSent += 1;
        if (platform === 'linkedin') summary.linkedinSent += 1;
        if (platform === 'google') summary.googleSent += 1;
      }
      if (result.error) summary.errors += 1;
    });
    if (eventSent) summary.anySent += 1;
  });

  return summary;
};

/**
 * Admin report: recent users with currentAttribution + conversionEventsSent.
 * Default window is last 30 days by startTime (excludes localhost).
 */
export const getAttributionConversionReport = async (days = 30) => {
  const users = await getAllUsers();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);
  const cutoffMs = cutoff.getTime();

  const rows = [];
  let totalUsersInWindow = 0;
  let withAttribution = 0;
  let withConversions = 0;
  const platformCounts = { meta: 0, google: 0, linkedin: 0 };
  const conversionSentCounts = { meta: 0, google: 0, linkedin: 0 };

  users.forEach((user) => {
    if (!isProductionHostname(user)) return;

    const startTime = convertFirebaseTimestamp(user.startTime);
    if (!startTime || isNaN(startTime.getTime()) || startTime.getTime() < cutoffMs) {
      return;
    }

    totalUsersInWindow += 1;

    const attribution = user.currentAttribution || null;
    const platforms = attributionPlatformsFrom(attribution || {});
    if (platforms.length > 0) {
      withAttribution += 1;
      platforms.forEach((p) => {
        platformCounts[p] += 1;
      });
    }

    const conversionEvents = Array.isArray(user.conversionEventsSent)
      ? user.conversionEventsSent
      : [];
    const conversionSummary = summarizeConversionEvents(conversionEvents);
    if (conversionSummary.anySent > 0) {
      withConversions += 1;
    }
    conversionSentCounts.meta += conversionSummary.metaSent;
    conversionSentCounts.linkedin += conversionSummary.linkedinSent;
    conversionSentCounts.google += conversionSummary.googleSent;

    const clickEvents = Array.isArray(user.clickEvents) ? user.clickEvents : [];

    rows.push({
      shortcode: user.shortcode || user.id,
      startTime,
      lastActive: convertFirebaseTimestamp(user.lastActive),
      hostname: user.hostname || null,
      name: user.userInfo?.name || null,
      email: user.userInfo?.email || null,
      phone: user.userInfo?.phone || null,
      currentScreen: user.currentScreen || null,
      completedScreens: Array.isArray(user.completedScreens)
        ? user.completedScreens
        : [],
      completedScreensCount: Array.isArray(user.completedScreens)
        ? user.completedScreens.length
        : 0,
      completedFunnel: !!user.completedFunnel,
      attribution,
      attributionPlatforms: platforms,
      clickEventsCount: clickEvents.length,
      clickEvents: clickEvents.slice(-25),
      conversionEvents,
      conversionSummary
    });
  });

  rows.sort((a, b) => (b.startTime?.getTime() || 0) - (a.startTime?.getTime() || 0));

  return {
    days,
    cutoff,
    generatedAt: new Date(),
    summary: {
      totalUsersInWindow,
      withAttribution,
      withConversions,
      platformCounts,
      conversionSentCounts
    },
    users: rows
  };
};

